import {
  Activity,
  AlertTriangle,
  BadgeCheck,
  Ban,
  Banknote,
  Bell,
  BarChart3,
  BellRing,
  BookOpen,
  Briefcase,
  Building2,
  Bus,
  Calculator,
  Calendar,
  CalendarDays,
  Car,
  Layers,
  RefreshCw,
  SpellCheck,
  CheckSquare,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CreditCard,
  Database,
  Dices,
  DollarSign,
  FileArchive,
  FileBarChart,
  FileCheck,
  FileCheck2,
  FileClock,
  FileSpreadsheet,
  FileText,
  Gavel,
  GitBranch,
  GitPullRequestArrow,
  GraduationCap,
  History,
  Home,
  IdCard,
  KeyRound,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  LockKeyhole,
  Mail,
  Map,
  Megaphone,
  MessagesSquare,
  Plug,
  Receipt,
  Replace,
  Route,
  Scale,
  ScanLine,
  School,
  ScrollText,
  Search,
  Send,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Truck,
  TrendingUp,
  Users,
  UserRound,
  Wallet,
  TableProperties,
  Workflow,
  CalendarCheck,
  Settings2,
  Tag,
  type LucideIcon,
  Globe,
  Navigation,
  CogIcon,
  Palette,
  Percent,
  UploadCloud,
  UserCheck,
  UserPlus,
} from 'lucide-react';

import { PAPEIS, PAPEIS_EXTERNOS, ehInterno, type Papel } from '../auth/papeis';
import { type TipoDeCredenciado } from '../auth/tipos';

/**
 * O mapa de navegação — fonte ÚNICA da verdade de rota e de menu.
 *
 * O roteador (App.tsx) e a barra lateral são os dois GERADOS daqui. É o que torna
 * verdadeira a regra de CLAUDE.md, "Proibido": nenhuma rota sem tela, nenhuma tela
 * fora da navegação. Enquanto as duas coisas nascerem da mesma lista, elas não
 * podem divergir — e o teste em mapa.spec.ts falha se alguém tentar.
 *
 * A LISTA DE MÓDULOS SAI DE docs/PLANO-DE-FASES.md, não do protótipo. O menu do
 * protótipo tem mais de sessenta itens, com uma arquitetura de informação que
 * ainda não foi validada pelo cliente (PRD, seção 1: "toda regra de negócio será
 * reconstruída módulo a módulo, com validação do cliente"). O plano de fases, sim,
 * é acordado — então é dele que vem cada grupo abaixo, na ordem das fases.
 *
 * Item de fase futura não é rota quebrada: ele existe, aparece no menu e abre a
 * tela "módulo em construção", dizendo em que fase entra
 * (docs/PLANO-DE-FASES.md, fase 1: "itens de menu ainda não construídos levam a um
 * estado 'módulo em construção' — nunca a uma tela quebrada").
 */

export interface ItemDeNavegacao {
  /** Caminho absoluto da rota. Único em todo o mapa. */
  caminho: string;
  titulo: string;
  icone: LucideIcon;
  /**
   * Fase do plano em que o módulo entra. `1` é o que já existe hoje; acima
   * disso, a tela é a de módulo em construção.
   */
  fase: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  /** Frase curta que a tela de módulo em construção mostra. */
  resumo?: string;
  /** Filhos do item, quando ele abre submenu em vez de ir direto pra uma rota. Só um nível. */
  filhos?: readonly Omit<ItemDeNavegacao, 'filhos'>[];
  /** Contagem exibida como badge ao lado do título. Vem de fonte real, nunca mockada aqui. */
  contagem?: number;
  /**
   * Rótulo de separador mostrado ANTES deste item, dentro de um submenu
   * (`filhos`) — ex.: "Analytics", separando etapas operacionais de telas
   * analíticas. Só tem efeito num item de `filhos`; ignorado num item de
   * primeiro nível.
   */
  separadorAntes?: string;
  /**
   * Raiz da área onde este item REALMENTE mora, quando é diferente da área
   * que o lista (cross-link, igual ao protótipo — ele não separa área por
   * guarda de papel do jeito que este mapa faz). Presente = a rota já existe
   * de verdade na área declarada aqui, e este item é só um atalho de menu
   * pra ela: `rotasDaArea` não gera `<Route>` duplicada, e o teste "todo item
   * vive dentro da própria área" aceita o item por causa deste campo.
   */
  areaReal?: string;
  /**
   * Quando definido, restringe ainda mais quem vê este item dentro da área —
   * além do RBAC da área inteira (`AreaDeNavegacao.papeis`). Existe porque
   * SME e DRE dividem a mesma área (`AREA_SME`, protótipo: `SmeLayout.tsx`,
   * `smeOnly`/`gestorOnly`) mas nem todo item do menu é dos dois papéis.
   */
  papeis?: readonly Papel[];
  /**
   * A própria tela bloqueia quem não pode, com a tela bloqueada do protótipo
   * (`PortalAccessGuard`), então a rota não repete `papeis`: se repetisse, quem
   * digitasse o endereço cairia na guarda genérica de rota e nunca veria o
   * texto da regra. `papeis` continua escondendo o item do menu e os links.
   * Só vale para tela que envolve o conteúdo numa guarda própria; a lista de
   * itens que usam este campo é conferida em `mapa.spec.ts`.
   */
  guardaNaTela?: true;
  /**
   * Rótulo alternativo mostrado pra quem só tem papel de DRE (sem SME nem
   * admin) — mesmo item, nome diferente, do jeito que o protótipo faz em
   * "Aprovações" → "Apontamentos" (`SmeLayout.tsx`, campo `dreLabel`).
   */
  tituloParaDre?: string;
  /**
   * Restringe o item ao tipo do credenciado logado, no Portal do
   * Transportador — igual ao protótipo (`PortalLayout.tsx`,
   * `isColetivo`/`isCoop`; front daqui, `usePortalAccess.ts`).
   * `'coletivo'` = PJ ou COOPERATIVA (some pra PF); `'cooperativa'` = só
   * COOPERATIVA.
   */
  condicionalPorTipo?: 'cooperativa' | 'coletivo';
  /**
   * Só para quem entra como pré-credenciado (`GET /eu`, `preCredenciado`): o
   * transportador contemplado no sorteio, com acesso antecipado e ainda sem
   * cadastro de credenciado (frente F10, D-28).
   */
  somentePreCredenciado?: boolean;
}

export interface GrupoDeNavegacao {
  titulo: string;
  itens: readonly ItemDeNavegacao[];
}

export interface AreaDeNavegacao {
  /** Raiz da área. É também o item de entrada dela. */
  raiz: string;
  /** Nome curto, para o cabeçalho e o título da aba. */
  nome: string;
  /** Ícone da área, usado no atalho "Ir para X" do menu da conta. */
  icone: LucideIcon;
  /** Papéis que entram na área. */
  papeis: readonly Papel[];
  grupos: readonly GrupoDeNavegacao[];
  /**
   * Atalhos em destaque na busca global ("Ações rápidas"), por caminho de um
   * item que já existe em `grupos` desta área. Opcional: sem isso, a busca
   * não mostra essa seção pra área. Aponta pra tela real mais próxima, nunca
   * pra uma ação que não existe (ex.: "Ordens de serviço", não "Nova ordem
   * de serviço" — criar ainda não existe, a lista já existe).
   */
  atalhosRapidos?: readonly string[];
}

/**
 * Papéis internos: DTP e rede de ensino (PRD, seção 4).
 *
 * Todos veem o MESMO menu na operação. O que muda entre eles é o recorte dos
 * dados, e isso é garantido no banco, não escondendo item de menu
 * (docs/AUTENTICACAO-E-ACESSO.md, seção 9). Filtrar item por papel aqui, hoje,
 * seria inventar regra de acesso que o cliente ainda não validou.
 */
const PAPEIS_INTERNOS = PAPEIS.filter((papel) => ehInterno(papel));

/** Administração do sistema. Only ADMIN e GESTOR_ADMIN — seção 9 do mesmo documento. */
const PAPEIS_ADMINISTRACAO: readonly Papel[] = ['ADMIN', 'GESTOR_ADMIN'];

/**
 * SME e DRE dividem a mesma área (`AREA_SME`), igual ao protótipo
 * (`SmeLayout.tsx`, `ProtectedSmeRoute.tsx`: os dois papéis entram no mesmo
 * `/sme`, sem portal próprio pra DRE). O que muda entre eles é visibilidade
 * por item (`ItemDeNavegacao.papeis`, `smeOnly`/`gestorOnly` do protótipo) e o
 * rótulo de um item (`tituloParaDre`), não a área.
 *
 * `GESTOR_ADMIN` também entra: está no guarda de rota real do protótipo
 * (`ProtectedSmeRoute.tsx`, `hasRole("GESTOR_ADMIN")`), não só no atalho do
 * menu de conta (`AppLayout.tsx`, `canSme`). `MASTER`/`MASTER_FINANCEIRO`
 * também estavam lá, mas não existem no conjunto de 15 papéis deste projeto.
 */
const PAPEIS_SME: readonly Papel[] = [
  'ADMIN',
  'GESTOR_ADMIN',
  'GESTOR_SME',
  'OPERADOR_SME',
  'GESTOR_DRE',
  'OPERADOR_DRE',
];
/**
 * Item visível só pra quem tem papel de SME (ou admin) — `smeOnly` do
 * protótipo. `GESTOR_ADMIN` entra: `ProtectedSmeOnlyRoute.tsx` calcula
 * `escopoGlobal = isAdmin || isFinanceiro || isSme`, e `isAdmin` sempre inclui
 * `hasRole("GESTOR_ADMIN")`, com ou sem `gestorOnly`.
 */
const PAPEIS_SOMENTE_SME: readonly Papel[] = [
  'ADMIN',
  'GESTOR_ADMIN',
  'GESTOR_SME',
  'OPERADOR_SME',
];
/**
 * Item visível só pra gestor de SME (ou admin) — `gestorOnly` + `smeOnly` do
 * protótipo, hoje usado só por "Auditoria do sistema".
 *
 * Esta lista já citou `isGestor = hasRole("GESTOR_SME")` (`SmeLayout.tsx:288`)
 * como prova de que o protótipo nunca deixa gestor de DRE entrar. A citação
 * estava errada, e Gabriel apontou na revisão da PR #125: aquela linha é de
 * componente de layout e decide o que aparece no menu, não quem acessa a rota.
 * Quem decide acesso é o guard da rota, e ele aceita mais papéis — ver
 * `PAPEIS_RELATORIOS_SME` abaixo. Só "Relatórios" foi conferido contra o guard
 * até agora; esta lista continua estreita porque ninguém conferiu a de
 * Auditoria, e alargar por analogia (`FINANCEIRO`, `GESTOR_DRE`) abriria
 * trilha de auditoria sem decisão.
 *
 * `GESTOR_ADMIN` é exceção a essa cautela: não é alargar por analogia, é
 * `isAdmin` (`ProtectedSmeOnlyRoute.tsx`), que o guard real sempre inclui,
 * `gestorOnly` ou não — o mesmo `isAdmin` que já justifica `GESTOR_ADMIN` em
 * `PAPEIS_SME` (a área inteira) e em `PAPEIS_SOMENTE_SME`, acima.
 */
const PAPEIS_SOMENTE_GESTOR_SME: readonly Papel[] = ['ADMIN', 'GESTOR_ADMIN', 'GESTOR_SME'];
/**
 * Quem acessa "Relatórios" (`/sme/relatorios`).
 *
 * O protótipo protege a rota com `<ProtectedSmeOnlyRoute gestorOnly
 * allowGestorDre>` (`App.tsx:889`), e o guard resolve
 * `allowed = isAdmin || isFinanceiro || isSmeGestor || (allowGestorDre &&
 * isGestorDre)`. `GESTOR_ADMIN` entra dentro de `isAdmin`, os outros quatro
 * papéis também existem aqui — gestor de DRE inclusive, que é justamente o
 * que `allowGestorDre` faz.
 */
const PAPEIS_RELATORIOS_SME: readonly Papel[] = [
  'ADMIN',
  'GESTOR_ADMIN',
  'FINANCEIRO',
  'GESTOR_SME',
  'GESTOR_DRE',
];

export const AREA_OPERACAO: AreaDeNavegacao = {
  raiz: '/operacao',
  nome: 'Operação',
  icone: Bus,
  papeis: PAPEIS_INTERNOS,
  atalhosRapidos: ['/operacao/ordens-de-servico', '/operacao/termos', '/operacao/apuracao'],
  grupos: [
    {
      /**
       * ANÁLISES E NOTIFICAÇÕES é o primeiro grupo da área, e o item que abre a
       * área é o próprio painel — foi assim que o protótipo desenhou, e foi o
       * que o PO decidiu em 2026-09-05: estas telas moram na Operação/DTP, não
       * na Administração. Vieram de `/admin/analises/*` (task #2981279, ONDA-CL1
       * a CL4) com rota, rótulo e ordem do protótipo.
       *
       * O grupo "Início" deixou de existir aqui: ele tinha um item só, o painel
       * da área, que o protótipo põe como primeiro item deste grupo.
       *
       * Um item só, não dois: no protótipo (`AppSidebar.tsx:33`), "Dashboard"
       * é o único rótulo de menu, e aponta pra raiz da área (`/`, com
       * `/dashboard` sendo redirect pra ela — `App.tsx:463`). "Visão Geral" é
       * só o título dentro da própria página, não item de menu separado. Os
       * dois itens que existiam aqui (um vazio em `/operacao`, outro com o
       * conteúdo em `/operacao/visao-geral`) eram duplicação nossa: fundidos
       * num só, rótulo "Dashboard", com o conteúdo das 4 abas dentro.
       */
      titulo: 'Análises e Notificações',
      itens: [
        {
          caminho: '/operacao',
          titulo: 'Dashboard',
          icone: LayoutDashboard,
          fase: 1,
          resumo:
            'Visão consolidada em quatro abas: operacional, financeiro, credenciamento e gestão.',
        },
        {
          caminho: '/operacao/relatorios',
          titulo: 'Relatórios',
          icone: FileBarChart,
          fase: 1,
          resumo:
            'Consolidações por diretoria, credenciado e competência, mais o backlog solicitado pela DTP.',
        },
        {
          caminho: '/operacao/kpis',
          titulo: 'KPIs',
          icone: Activity,
          fase: 1,
          resumo: 'Tendência mensal, ranking de credenciados e concentração de penalidades.',
        },
        {
          caminho: '/operacao/mapa',
          titulo: 'Mapa',
          icone: Map,
          fase: 1,
          resumo:
            'Cobertura territorial por diretoria. O desenho cartográfico depende de decisão de biblioteca.',
        },
        {
          caminho: '/operacao/notificacoes',
          titulo: 'Notificações',
          icone: Bell,
          fase: 1,
          resumo:
            'Avisos da operação e comunicados institucionais, com filtro por tipo, prioridade e período.',
        },
      ],
    },
    {
      /**
       * "Cadastros" no protótipo (`AppSidebar.tsx`), não "Credenciamento" —
       * lá "Credenciamento" é só o item com submenu de Inscrições/Funil/
       * Métricas, abaixo. Rótulos iguais aqui colidiam: dois botões de
       * mesmo nome acessível na mesma barra lateral (achado real, quebrou
       * `CascaDeArea.spec.tsx` ao testar), não só repetição visual.
       */
      titulo: 'Cadastros',
      itens: [
        {
          caminho: '/operacao/credenciados',
          titulo: 'Credenciados',
          icone: Building2,
          fase: 1,
          resumo: 'Cadastro das empresas e cooperativas credenciadas.',
        },
        // Ordem do protótipo: credenciados, condutores, monitores, titulares.
        // Titulares vinha em segundo aqui, e desceu para o quarto lugar.
        {
          caminho: '/operacao/condutores',
          titulo: 'Condutores',
          icone: UserCheck,
          fase: 1,
          resumo: 'Condutores habilitados e sua documentação.',
        },
        {
          caminho: '/operacao/monitores',
          titulo: 'Monitores',
          icone: UserRound,
          fase: 1,
          resumo: 'Monitores de acompanhamento dos estudantes.',
        },
        {
          caminho: '/operacao/titulares',
          titulo: 'Titulares',
          icone: IdCard,
          fase: 1,
          resumo: 'Titulares responsáveis pelo credenciamento.',
        },
        {
          // Ordem do protótipo (`referenciaDoPrototipo.ts:158-162`): quinto
          // item de Cadastros. Morava em "Frota e contratos", grupo que o
          // protótipo não tem.
          caminho: '/operacao/veiculos',
          titulo: 'Veículos',
          icone: Bus,
          fase: 1,
          resumo: 'Frota, licenças, vistorias e apólices.',
        },
      ],
    },
    {
      /**
       * Grupo "Operacional" — no protótipo (`AppSidebar.tsx`) é um grupo só,
       * achatado, com Termos e Ordens de Serviço dentro dele. Aqui ficou
       * dividido em vários grupos por fase (histórico do repositório, não
       * decisão desta tarefa); Termos, Ordens de Serviço e Fiscalização
       * voltaram pra cá pra bater com o protótipo, mesmo o `fase` de cada um
       * continuando o que já era (a organização por fase de
       * `PLANO-DE-FASES.md` não muda, só a posição no menu).
       *
       * "Fiscalização" bate com o protótipo agora: item único, plano, sem
       * `children` (`AppSidebar.tsx`, `url: "/fiscalizacao-hub"`), levando a
       * um hub com abas internas (Ocorrências/Multas e recursos/Cobranças —
       * `FiscalizacaoHub.tsx`, que resolve a sub-rota certa por
       * `useLocation()`). Antes eram 3 itens de menu (3 rotas irmãs); virou
       * 1 nesta entrega. "Credenciamento" (submenu, no protótipo) continua
       * divergente: mudar isso reestruturaria rota e teste de outra pessoa,
       * não é decisão de tela.
       */
      titulo: 'Operacional',
      itens: [
        {
          caminho: '/operacao/termos',
          titulo: 'Termos',
          icone: FileText,
          fase: 1,
          resumo: 'Termos de credenciamento, aditivos e garantias.',
          filhos: [
            {
              caminho: '/operacao/termos',
              titulo: 'Todos os termos',
              icone: FileText,
              fase: 1,
            },
            {
              caminho: '/operacao/termos/vencimentos',
              titulo: 'Vencimentos do mês',
              icone: Calendar,
              fase: 1,
              resumo: 'Termos com renovação prevista no mês selecionado.',
            },
          ],
        },
        {
          caminho: '/operacao/ordens-de-servico',
          titulo: 'Ordens de serviço',
          icone: ClipboardList,
          fase: 1,
          // A OS da jornada é do DTP (F13): a API recusa os outros papéis internos.
          papeis: ['ADMIN', 'GESTOR_ADMIN', 'GESTOR', 'OPERADOR', 'AUDITOR'],
          resumo: 'Emissão, ativação e documento da OS: veículo, condutor, monitor e DRE.',
        },
        {
          /**
           * Item com submenu "Credenciamento", igual ao protótipo
           * (`AppSidebar.tsx`, `url: "/credenciamento/inscricoes"`, ícone
           * `UserPlus`): Inscrições / separador "Analytics" / Funil do
           * portal público / Métricas de inscrições.
           */
          caminho: '/operacao/inscricoes',
          titulo: 'Credenciamento',
          icone: UserPlus,
          fase: 1,
          // A triagem é do DTP (F07): a API recusa os outros papéis internos.
          papeis: ['ADMIN', 'GESTOR_ADMIN', 'GESTOR', 'OPERADOR', 'AUDITOR'],
          resumo: 'Fila de análise documental do credenciamento, funil e métricas.',
          filhos: [
            {
              caminho: '/operacao/inscricoes',
              titulo: 'Inscrições',
              icone: ClipboardCheck,
              fase: 1,
            },
            {
              /**
               * Sorteio do credenciamento (frente F08): um por ciclo, depois
               * da triagem. Não existe no protótipo — acréscimo registrado em
               * `conformidadeComOPrototipo.spec.ts`. O painel de um ciclo,
               * `/operacao/sorteios/:cicloId`, é sub-rota da própria tela
               * (`SorteiosArea.tsx`), como o detalhe de Inscrições.
               */
              caminho: '/operacao/sorteios',
              titulo: 'Sorteios',
              icone: Dices,
              fase: 1,
              // Leitura do DTP; os atos (preparar, executar, publicar, chamar
              // suplente) a API só aceita de ADMIN, GESTOR_ADMIN e GESTOR.
              papeis: ['ADMIN', 'GESTOR_ADMIN', 'GESTOR', 'OPERADOR', 'AUDITOR'],
              resumo: 'Preparo, execução, resultado, ata e suplentes do sorteio de cada ciclo.',
            },
            {
              /**
               * Documentação do pré-credenciado (frente F10, D-28): fila de
               * análise por requisito e dossiê de cada veículo contemplado.
               * Não existe no protótipo — acréscimo registrado em
               * `conformidadeComOPrototipo.spec.ts`. O dossiê,
               * `/operacao/pre-credenciamento/:id`, é sub-rota da própria tela.
               */
              caminho: '/operacao/pre-credenciamento',
              titulo: 'Documentação do pré-credenciado',
              icone: FileCheck,
              fase: 1,
              papeis: ['ADMIN', 'GESTOR_ADMIN', 'GESTOR', 'OPERADOR', 'AUDITOR'],
              resumo: 'Fila de análise por requisito e dossiê de cada veículo pré-credenciado.',
            },
            {
              caminho: '/operacao/inscricoes/funil',
              titulo: 'Funil do portal público',
              icone: Activity,
              fase: 1,
              resumo: 'Conversão e abandono por etapa do wizard público de autocadastro.',
              separadorAntes: 'Analytics',
            },
            {
              caminho: '/operacao/inscricoes/metricas',
              titulo: 'Métricas de inscrições',
              icone: Activity,
              fase: 1,
              resumo: 'Volume, taxa de aprovação e tempo de decisão da fila.',
            },
          ],
        },
        {
          caminho: '/operacao/convocacoes',
          titulo: 'Convocações',
          icone: Send,
          fase: 1,
          resumo: 'Convocações do processo de credenciamento.',
        },
        {
          caminho: '/operacao/aprovacoes',
          titulo: 'Aprovações DTEG',
          icone: ShieldCheck,
          fase: 1,
          resumo: 'Fila de aprovações pendentes da DTEG.',
        },
        {
          caminho: '/operacao/consultas',
          titulo: 'Dashboard de consultas',
          icone: Search,
          fase: 1,
          resumo: 'Consultas de apoio ao credenciamento.',
        },
        {
          caminho: '/operacao/importacao-eol',
          titulo: 'Importação EOL',
          icone: UploadCloud,
          fase: 1,
          resumo: 'Importação de dados do sistema EOL.',
        },
        {
          caminho: '/operacao/fiscalizacao',
          titulo: 'Fiscalização',
          icone: AlertTriangle,
          fase: 1,
          resumo: 'Ocorrências, multas, recursos e cobranças decorrentes de penalidade.',
          /**
           * Exceção deliberada, pedida pelo PO: mantém um número ilustrativo
           * aqui pra validar visualmente o badge (inclusive o corte em
           * "99+"), contra a regra do CLAUDE.md sobre dado de exemplo. Não é
           * esquecimento — não remover sem confirmar com o PO de novo. Antes
           * ficava no item "Ocorrências"; migrou pra cá quando os 3 itens
           * viraram 1 só, igual ao protótipo (`AppSidebar.tsx`,
           * `url: "/fiscalizacao-hub"`, item plano, sem `children`).
           */
          contagem: 128,
        },
        {
          caminho: '/operacao/descredenciamento',
          titulo: 'Bloqueios e Cancelamentos',
          icone: Ban,
          fase: 1,
          resumo: 'Bloqueios e cancelamentos de credenciamento.',
        },
      ],
    },
    {
      titulo: 'Frota e contratos',
      itens: [
        {
          caminho: '/operacao/aptidao-recursos',
          titulo: 'Aptidão de recursos',
          icone: BadgeCheck,
          fase: 3,
          resumo: 'Elegibilidade temporal de frota e equipe para ordens de serviço.',
        },
        {
          caminho: '/operacao/manutencao-recursos',
          titulo: 'Manutenção de recursos',
          icone: FileClock,
          fase: 3,
          resumo: 'Versões futuras, substituições e inativações com histórico.',
        },
      ],
    },
    {
      titulo: 'Operação',
      itens: [
        {
          caminho: '/operacao/escolas',
          titulo: 'Escolas e diretorias',
          icone: Map,
          fase: 4,
          resumo: 'Unidades escolares e diretorias regionais de ensino.',
        },
        {
          caminho: '/operacao/rotas',
          titulo: 'Rotas',
          icone: Route,
          fase: 4,
          resumo: 'Rotas de atendimento dos estudantes.',
        },
        {
          caminho: '/operacao/apontamentos-aprovados',
          titulo: 'Apontamentos aprovados',
          icone: ClipboardList,
          fase: 5,
          resumo: 'Consulta DTP e devolução formal à SME sem edição de linhas.',
        },
        {
          caminho: '/operacao/composicoes-competencia',
          titulo: 'Composição da competência',
          icone: ClipboardList,
          fase: 5,
          resumo: 'Memória versionada de dias, estudantes, quilometragem, ajustes e retenções.',
        },
        {
          caminho: '/operacao/atestes-competencia',
          titulo: 'Dupla alçada',
          icone: ShieldCheck,
          fase: 5,
          resumo: 'Ateste técnico com duas identidades independentes e histórico de revogação.',
        },
        {
          caminho: '/operacao/conciliacao-referencia',
          titulo: 'Conciliar e selar',
          icone: FileCheck2,
          fase: 5,
          resumo:
            'Importação da referência oficial, conciliação de hashes e selo imutável da competência.',
        },
        {
          caminho: '/operacao/medicoes',
          titulo: 'Medições e demonstrativos',
          icone: Receipt,
          fase: 5,
          resumo: 'Consolidação versionada, ajustes aprovados e rastreabilidade até o apontamento.',
        },
        {
          caminho: '/operacao/validacao-ordens-servico',
          titulo: 'Validar disponibilidade',
          icone: ClipboardList,
          fase: 4,
          resumo: 'Conflitos, vigência e reserva atômica dos recursos da OS.',
        },
        {
          caminho: '/operacao/ativacao-ordens-servico',
          titulo: 'Ativar e comunicar OS',
          icone: ClipboardList,
          fase: 4,
          resumo: 'Ativação idempotente e comunicação aos recortes envolvidos.',
        },
        {
          caminho: '/operacao/manutencao-ordens-servico',
          titulo: 'Substituir ou cancelar OS',
          icone: ClipboardList,
          fase: 4,
          resumo: 'Decisão versionada sobre substituições e cancelamentos futuros.',
        },
        {
          /**
           * Migrada do protótipo por decisão do dono em 2026-08-26, antes da
           * especificação formal da Fase 4 alcançar este item — ver
           * docs/ALVOS.md. Reúne dia letivo, vistoria, vencimento de
           * documento, fim de termo e prazo legal; a rota agregadora ainda
           * não existe na API.
           */
          caminho: '/operacao/calendario',
          titulo: 'Calendário operacional',
          icone: CalendarDays,
          fase: 4,
          resumo: 'Período letivo, vistorias, vencimentos, termos e prazos legais.',
        },
      ],
    },
    {
      /**
       * UM GRUPO FINANCEIRO SÓ, como no protótipo.
       *
       * Existiam dois — "Financeiro" e "Apuração e financeiro" —, com os oito
       * itens financeiros do protótipo distribuídos entre eles, e cada um com
       * um item chamado "Ciclo financeiro" apontando para rota diferente.
       * Decisão do PO em 2026-09-05: um grupo só, e a rota que prevalece para o
       * ciclo é `/operacao/financeiro/ciclo-financeiro` — a que tem tela
       * construída. `/operacao/financeiro/ciclo` saiu do menu.
       */
      titulo: 'Financeiro',
      itens: [
        // Os dez primeiros na ORDEM DO PROTÓTIPO. Depois deles vêm os itens de
        // fase futura, que o protótipo não tem e que hoje nem aparecem no menu.
        {
          caminho: '/operacao/financeiro/painel-executivo',
          titulo: 'Painel executivo',
          icone: BarChart3,
          fase: 1,
          resumo: 'Indicadores financeiros consolidados do programa.',
        },
        {
          caminho: '/operacao/financeiro/indicadores-mensais',
          titulo: 'Indicadores mensais',
          icone: FileSpreadsheet,
          fase: 1,
          resumo: 'Séries mensais de valores apurados, aprovados e pagos.',
        },
        {
          caminho: '/operacao/financeiro/fechamento-mensal',
          titulo: 'Fechamento mensal',
          icone: LockKeyhole,
          fase: 1,
        },
        {
          caminho: '/operacao/financeiro/resumo-aprovacao-sme',
          titulo: 'Resumo para aprovação SME',
          icone: FileCheck2,
          fase: 1,
          resumo: 'Resumo do mês por DRE para aprovação da SME.',
        },
        {
          caminho: '/operacao/financeiro/apuracao-mensal',
          titulo: 'Apuração mensal',
          icone: Calculator,
          fase: 1,
        },
        {
          // Ordem do protótipo (`referenciaDoPrototipo.ts`, `AppSidebar.tsx:106`):
          // logo depois de Apuração Mensal, antes de Cobranças DAMSP. Estava
          // bem mais abaixo no grupo, fora de ordem.
          caminho: '/operacao/financeiro/ciclo-financeiro',
          titulo: 'Ciclo financeiro',
          icone: Route,
          fase: 1,
        },
        {
          // fase corrigida de 5 para 1: existe tela real construída
          // (`CobrancasDamspPage.tsx`, registrada em `App.tsx:322`), estava
          // marcada como futura e a decisão 5 (fase futura some do menu)
          // escondia uma tela que já funciona.
          caminho: '/operacao/financeiro/cobrancas',
          titulo: 'Cobranças DAMSP',
          icone: Receipt,
          fase: 1,
          resumo: 'Cobranças administrativas emitidas via DAMSP.',
        },
        {
          caminho: '/operacao/financeiro/sof',
          titulo: 'SOF',
          icone: Landmark,
          fase: 1, // tela real: SofPage (App.tsx), estava marcada fase futura por engano
          resumo: 'Integração com o Sistema de Orçamento e Finanças (SOF).',
        },
        {
          caminho: '/operacao/financeiro/auditoria',
          titulo: 'Auditoria financeira',
          icone: ShieldCheck,
          fase: 1,
          resumo: 'Trilha de alterações nos registros financeiros.',
        },
        {
          // O protótipo (`referenciaDoPrototipo.ts:314-317`, `AppSidebar.tsx:114`)
          // chama esta função de "Ateste de Medição", na rota `motor-calculo` —
          // é a MESMA funcionalidade, só renomeada, não duas. Aqui a tela real
          // (modal de aprovação em lote, status) foi construída em
          // `/operacao/financeiro/ateste-medicao`, tratando por engano como
          // feature nova; `motor-calculo` nunca teve tela própria, só cai no
          // hub genérico (`FinanceiroInicio`). Fica a rota com conteúdo de
          // verdade — mover pra `motor-calculo` reconstruiria o que já
          // funciona. Divergência de rota registrada em
          // `conformidadeComOPrototipo.spec.ts` (`DIVERGENCIAS_ACEITAS`).
          caminho: '/operacao/financeiro/ateste-medicao',
          titulo: 'Ateste de Medição',
          icone: Calculator,
          fase: 1, // tela real: AtesteDeMedicaoPage (App.tsx), estava marcada fase futura por engano
          resumo: 'Simulador do cálculo de remuneração por credenciado.',
        },
        {
          caminho: '/operacao/financeiro/tabela-precos',
          titulo: 'Tabela de Preços Contratuais',
          icone: TableProperties,
          fase: 1,
          resumo:
            'Componentes da remuneração, parâmetros de retenções (INSS e ISS) e faixas progressivas do IR.',
        },
        {
          /**
           * J-08 S05, a etapa entre a medição consolidada (`/operacao/medicoes`)
           * e a exportação (`/operacao/pagamentos`), contra o BFF de verdade
           * (`j08-s05-empenhar-liquidar`). A tela (`EmpenharLiquidarPage`)
           * nunca deixou de estar em `TELAS_PRONTAS`, mas passou um mês sem
           * rota: a PR #70 reescreveu NO LUGAR o item "Remuneração" — que na
           * base da branch dela ainda era só roadmap, sem tela — como "Ciclo
           * financeiro"; a tela nasceu em paralelo no develop (dd69d95), e o
           * merge juntou as duas coisas. Não foi decisão de retirar a etapa.
           *
           * Não é a tela do protótipo para empenho e liquidação: essa é
           * `EmpenhoLiquidacaoPage`, ainda não migrada, e responde por
           * `/operacao/financeiro/empenho-liquidacao` logo abaixo. Por isso o
           * título aqui é o da própria tela, e não o daquele item.
           *
           * Fase futura, como as irmãs da J-08 neste grupo: a rota existe, o
           * menu não a mostra (decisão 5 do PO, 2026-09-05). Pô-la no menu é
           * acréscimo ao protótipo, e isso é decisão do dono.
           */
          caminho: '/operacao/remuneracao',
          titulo: 'Empenhar e liquidar medição',
          icone: Banknote,
          fase: 5,
          resumo: 'Dotação, reserva de empenho, demonstrativo anexado e confirmação da liquidação.',
        },
        {
          caminho: '/operacao/pagamentos',
          titulo: 'Pagamentos',
          icone: Banknote,
          fase: 5,
          resumo: 'Exportação orçamentária, retorno e efetivação bancária.',
        },
        {
          caminho: '/operacao/conciliacao-pagamentos',
          titulo: 'Conciliação de pagamentos',
          icone: Banknote,
          fase: 5,
          resumo: 'Matching, exceções e demonstrativo final publicado.',
        },
        {
          caminho: '/operacao/financeiro',
          titulo: 'Visão geral financeira',
          icone: Home,
          fase: 5,
          resumo: 'Acompanhamento das competências, fechamentos, ciclo financeiro e SOF.',
        },
        {
          caminho: '/operacao/financeiro/apontamentos',
          titulo: 'Apontamentos',
          icone: ClipboardList,
          fase: 5,
          resumo: 'Lançamento diário de apontamentos financeiros por competência.',
        },
        {
          caminho: '/operacao/financeiro/apontamento-grid',
          titulo: 'Grade de apontamentos',
          icone: ClipboardList,
          fase: 5,
          resumo: 'Grade consolidada de apontamentos por competência e recorte.',
        },
        {
          caminho: '/operacao/financeiro/acompanhamento-mensal',
          titulo: 'Acompanhamento mensal',
          icone: CheckSquare,
          fase: 5,
          resumo: 'Acompanhamento do andamento da competência mês a mês.',
        },
        {
          // Dona da rota, invisível aqui de propósito: o protótipo não lista
          // este item no Financeiro do DTP (`referenciaDoPrototipo.ts`, 11
          // itens), só dentro do menu de SME — visível só de lá (cross-link,
          // `AREA_SME`, mesmo caminho, `areaReal: '/operacao'`).
          caminho: '/operacao/financeiro/aprovacao-sme',
          titulo: 'Aprovações',
          icone: UserCheck,
          fase: 5,
          resumo: 'Fila de aprovação da SME sobre a competência apurada.',
        },
        {
          caminho: '/operacao/financeiro/medicoes',
          titulo: 'Medições',
          icone: Receipt,
          fase: 5,
          resumo: 'Medições financeiras consolidadas por competência.',
        },
        {
          caminho: '/operacao/financeiro/demonstrativos',
          titulo: 'Demonstrativos',
          icone: FileText,
          fase: 5,
          resumo: 'Demonstrativos financeiros gerados por competência.',
        },
        {
          // Mesmo caso de "Aprovações" acima: dona da rota, invisível aqui de
          // propósito, visível só via cross-link em `AREA_SME`.
          caminho: '/operacao/financeiro/solicitacoes-sme',
          titulo: 'Solicitações',
          icone: UserCheck,
          fase: 5,
          resumo: 'Pedidos abertos pela SME à equipe DTP.',
        },
        {
          caminho: '/operacao/financeiro/fechamento-contabil',
          titulo: 'Fechamento contábil',
          icone: LockKeyhole,
          fase: 5,
          resumo: 'Fechamento contábil da competência, após o fechamento mensal.',
        },
        {
          caminho: '/operacao/financeiro/selamento-mensal',
          titulo: 'Selamento mensal',
          icone: LockKeyhole,
          fase: 5,
          resumo: 'Selamento (trava) da competência mensal já fechada.',
        },
        {
          caminho: '/operacao/financeiro/empenho-liquidacao',
          titulo: 'Empenho e liquidação',
          icone: Banknote,
          fase: 5,
          resumo: 'Empenho e liquidação orçamentária da competência.',
        },
        {
          // Aba do hub SOF (`SofTabsNav.tsx`), não item de menu independente —
          // o protótipo tem uma página só ("SOF") com 6 abas internas por
          // estado local; aqui virou 6 rotas por navegação (FIN-030/031), mas
          // só uma aparece na barra lateral, igual ao protótipo. Esta ficava
          // fase 1 enquanto as 5 irmãs (status, conciliação, relatórios,
          // histórico) já eram fase 5 — inconsistente. A tela continua
          // funcionando normalmente, alcançada pela aba dentro do hub SOF.
          caminho: '/operacao/financeiro/sof/reservas',
          titulo: 'Reservas SOF',
          icone: Landmark,
          fase: 5,
          resumo: 'Reservas orçamentárias vinculadas ao SOF.',
        },
        {
          caminho: '/operacao/financeiro/sof/status',
          titulo: 'Status SOF',
          icone: Activity,
          fase: 5,
          resumo: 'Status da integração e dos lotes enviados ao SOF.',
        },
        {
          caminho: '/operacao/financeiro/sof/lotes',
          titulo: 'Lotes SOF',
          icone: FileCheck2,
          fase: 5,
          resumo: 'Lotes de empenho e liquidação enviados ao SOF.',
        },
        {
          caminho: '/operacao/financeiro/sof/conciliacao',
          titulo: 'Conciliação SOF',
          icone: CheckSquare,
          fase: 5,
          resumo: 'Conciliação dos lançamentos do SOF com o TEG.',
        },
        {
          caminho: '/operacao/financeiro/sof/relatorios',
          titulo: 'Relatórios fiscais',
          icone: FileText,
          fase: 5,
          resumo: 'Relatórios fiscais extraídos do SOF.',
        },
        {
          caminho: '/operacao/financeiro/sof/historico',
          titulo: 'Histórico SOF',
          icone: ScrollText,
          fase: 5,
          resumo: 'Histórico de integrações e envios ao SOF.',
        },
        {
          caminho: '/operacao/financeiro/calendario-prazos',
          titulo: 'Calendário de prazos',
          icone: Calendar,
          fase: 5,
          resumo: 'Calendário de prazos do ciclo financeiro mensal.',
        },
      ],
    },
    {
      titulo: 'Fiscalização',
      itens: [
        {
          caminho: '/operacao/ocorrencias',
          titulo: 'Ocorrências',
          icone: AlertTriangle,
          fase: 6,
          resumo: 'Registro e apuração de ocorrências.',
          /**
           * Exceção deliberada, pedida pelo PO: mantém um número ilustrativo
           * aqui pra validar visualmente o badge (inclusive o corte em
           * "99+"), contra a regra do CLAUDE.md sobre dado de exemplo. Não é
           * esquecimento — não remover sem confirmar com o PO de novo.
           */
          contagem: 128,
        },
        {
          caminho: '/operacao/penalidades',
          titulo: 'Multas e recursos',
          icone: Gavel,
          fase: 6,
          resumo: 'Multas, defesas e recursos.',
        },
        {
          caminho: '/operacao/defesas/instrucao',
          titulo: 'Instrução de defesas',
          icone: Gavel,
          fase: 6,
          resumo: 'Pareceres e impedimentos dos autos de defesa.',
        },
        {
          caminho: '/operacao/defesas/decisao',
          titulo: 'Decisão em alçada',
          icone: Gavel,
          fase: 6,
          resumo: 'Decisões assinadas e efeitos finais.',
        },
        {
          caminho: '/operacao/cobrancas',
          titulo: 'Cobranças',
          icone: Receipt,
          fase: 6,
          resumo: 'Cobranças decorrentes de penalidade.',
        },
        {
          caminho: '/operacao/penalidades/aplicacao',
          titulo: 'Aplicação de penalidades',
          icone: Gavel,
          fase: 6,
          resumo: 'Vigência cadastral, comunicações e bloqueios operacionais.',
        },
        {
          caminho: '/operacao/solicitacoes/triagem',
          titulo: 'Triagem de solicitações',
          icone: ListChecks,
          fase: 6,
          resumo: 'Completude, complemento, redistribuição e passagem para decisão.',
        },
        {
          caminho: '/operacao/solicitacoes/decisao',
          titulo: 'Decisão de solicitações',
          icone: Gavel,
          fase: 6,
          resumo: 'Decisão fundamentada e aplicação atômica da mudança aprovada.',
        },
        {
          titulo: 'Encerramento de solicitações',
          caminho: '/operacao/solicitacoes/encerramento',
          icone: FileCheck2,
          resumo: 'Prazo, resultado e indicadores.',
          fase: 6,
        },
      ],
    },
  ],
};

export const AREA_ADMIN: AreaDeNavegacao = {
  raiz: '/admin',
  nome: 'Administração',
  icone: Shield,
  papeis: PAPEIS_ADMINISTRACAO,
  grupos: [
    {
      titulo: 'Início',
      itens: [{ caminho: '/admin', titulo: 'Página inicial', icone: Home, fase: 1 }],
    },
    {
      titulo: 'Portal Público',
      itens: [
        {
          caminho: '/admin/portal/paginas',
          titulo: 'Páginas',
          icone: Globe,
          fase: 1,
        },
        {
          caminho: '/admin/portal/navegacao',
          titulo: 'Navegação',
          icone: Navigation,
          fase: 1,
        },
        {
          caminho: '/admin/portal/configuracoes',
          titulo: 'Configurações Portal',
          icone: CogIcon,
          fase: 1,
        },
      ],
    },
    {
      /**
       * Migrada do protótipo por decisão do dono em 2026-08-26, antes da
       * especificação formal da Fase 2 alcançar este item — ver docs/ALVOS.md.
       * Como o módulo `documentos` ainda não existe na API, a tela mostra o
       * estado de erro honesto em vez de fingir fila vazia.
       */
      titulo: 'Documentos',
      itens: [
        {
          caminho: '/admin/moderacao-ocr',
          titulo: 'Moderação OCR',
          icone: ScanLine,
          // Fase 1, e não 2: a tela foi construída na migração de 2026-08-26 e
          // tem rota registrada. Como item de fase futura não é renderizado, a
          // marcação de fase 2 fazia o grupo "Documentos" inteiro sumir do menu
          // — este é o único item dele — e a tela só se alcançava digitando o
          // endereço. Tela sem entrada na navegação é proibida (CLAUDE.md).
          fase: 1,
          resumo: 'Confere e aprova dado extraído por OCR dos documentos enviados.',
        },
      ],
    },
    {
      titulo: 'Controle de Acesso',
      itens: [
        {
          caminho: '/admin/usuarios',
          titulo: 'Usuários',
          icone: Users,
          fase: 1,
          resumo: 'Pessoas com acesso, perfil de acesso, convites e segurança da conta.',
        },
        {
          caminho: '/admin/perfis',
          titulo: 'Perfis de Acesso',
          icone: KeyRound,
          fase: 1,
          resumo:
            'Gestão dos perfis de permissão do sistema. Exibe os perfis do sistema (roles fixas) e permite criar perfis customizados com matriz granular de permissões por módulo e ação.',
        },
      ],
    },
    {
      titulo: 'Governança',
      itens: [
        {
          caminho: '/admin/governanca-institucional',
          titulo: 'Governança Institucional',
          icone: ShieldCheck,
          fase: 1,
          resumo:
            'Painel consolidado de governança da DTEG: indicadores de conformidade, auditoria, processos abertos, prazos legais e marcos institucionais.',
        },
        {
          caminho: '/admin/politica-sgtp',
          titulo: 'Política SGTP × Manual',
          icone: Scale,
          fase: 1,
          resumo:
            'Regras de prevalência entre o SGTP (sistema legado) e o sistema TEG: o que vence em caso de divergência (placa, condutor, escola, vínculo).',
        },
        {
          caminho: '/admin/auditoria',
          titulo: 'Auditoria',
          icone: ScrollText,
          fase: 1,
          resumo: 'Trilha imutável, exportação registrada e achados separados.',
        },
      ],
    },
    {
      /**
       * Os quatro itens abaixo de "Parâmetros" foram migrados do protótipo por
       * decisão do dono em 2026-08-26 — exceção explícita ao processo normal
       * (Levantar → Especificar aprovado pelo cliente → Banco → API →
       * Interface) de docs/PLANO-DE-FASES.md, registrada em docs/ALVOS.md.
       * Nenhum tem módulo na API ainda: cada `dados.ts` chama a rota real e
       * mostra erro honesto enquanto ela não existir, e nenhuma ação de
       * gravação finge sucesso (mesmo padrão de
       * `modulos/operacao/comum/cadastroIndisponivel.ts`).
       */
      titulo: 'Parametrização',
      itens: [
        /**
         * Parametrização foi ANTECIPADA da fase 8 a pedido do cliente
         * (2026-08-22), e por isso os itens abaixo estão como `fase: 1` — o
         * campo significa "o que já existe hoje", não a fase de origem no
         * plano. `docs/PLANO-DE-FASES.md` registra a antecipação, para que
         * plano e código não divirjam em silêncio.
         *
         * A SUBDIVISÃO EM CENTRO + QUATRO HUBS VEIO DO PROTÓTIPO, e é a única
         * parte deste mapa que não sai de `docs/PLANO-DE-FASES.md`. O PRD
         * autoriza aproveitar a interface do protótipo (seção 1, "regra
         * inegociável"), mas a arquitetura de informação dele ainda não passou
         * por validação do cliente — o risco está nomeado no próprio PRD
         * ("regra de negócio do protótipo estar errada → retrabalho"). Se o
         * cliente reorganizar os hubs, muda aqui e as rotas acompanham.
         *
         * O caminho é `/admin/parametros`, o que este mapa já declarava — não
         * o `/admin/parametrizacao` do protótipo.
         */
        {
          caminho: '/admin/parametros',
          // LayoutGrid bate com o que a própria tela do protótipo comunica
          // (visão geral em grade dos parâmetros).
          titulo: 'Centro de Parametrização',
          icone: LayoutGrid,
          fase: 1,
        },
        {
          // Ícone do protótipo (`parametrizacaoNavigation.ts:53`): Database.
          caminho: '/admin/parametros/cadastros',
          titulo: 'Cadastros & Domínios',
          icone: Database,
          fase: 1,
          // Ícones do protótipo (`parametrizacaoNavigation.ts:56-67`) — os
          // quatro itens de veículo usavam o mesmo ícone (Bus) pra coisas
          // diferentes; os demais também divergiam.
          filhos: [
            {
              caminho: '/admin/parametros/cadastros/marcas-veiculo',
              titulo: 'Marcas de Veículo',
              icone: Car,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/cadastros/modelos-veiculo',
              titulo: 'Modelos de Veículo',
              icone: Car,
              fase: 1,
            },
            // Versões, fila de grafias e sincronização com a fonte oficial: não
            // existem no protótipo (ordem V1 de 04/10/2026, premissa do dono de
            // parametrizar marca, modelo e versão e sincronizá-los com a fonte).
            {
              caminho: '/admin/parametros/cadastros/versoes-veiculo',
              titulo: 'Versões de Veículo',
              icone: Layers,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/cadastros/grafias-veiculo',
              titulo: 'Fila de Grafias de Veículo',
              icone: SpellCheck,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/cadastros/sincronizacao-veiculos',
              titulo: 'Sincronização com a Fonte Oficial',
              icone: RefreshCw,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/cadastros/tipos-veiculo',
              titulo: 'Tipos de Veículo',
              icone: Truck,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/cadastros/cores-veiculo',
              titulo: 'Cores de Veículo',
              icone: Palette,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/cadastros/tipos-unidade-escolar',
              titulo: 'Tipos de Unidade Escolar',
              icone: School,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/cadastros/categorias-cnh',
              titulo: 'Categorias CNH',
              icone: IdCard,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/cadastros/tipos-vinculo',
              titulo: 'Tipos de Vínculo Empregatício',
              icone: Briefcase,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/cadastros/cursos-obrigatorios',
              titulo: 'Cursos Obrigatórios',
              icone: GraduationCap,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/cadastros/tipos-ocorrencia',
              titulo: 'Tipos de Ocorrência',
              icone: AlertTriangle,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/cadastros/motivos-cancelamento-os',
              titulo: 'Motivos de Cancelamento de OS',
              icone: Ban,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/cadastros/motivos-substituicao',
              titulo: 'Motivos de Substituição de Recurso',
              icone: Replace,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/cadastros/tipos-publicacao',
              titulo: 'Tipos de Publicação de Documento',
              icone: BookOpen,
              fase: 1,
            },
          ],
        },
        {
          // Ícone do protótipo (`parametrizacaoNavigation.ts:73`): Database.
          caminho: '/admin/parametros/referencia',
          titulo: 'Cadastros de Referência',
          icone: Database,
          fase: 1,
          filhos: [
            {
              // Ícones do protótipo (`parametrizacaoNavigation.ts:76-77`):
              // estavam trocados entre si (Escolas usava o ícone de
              // Seguradoras, e vice-versa).
              caminho: '/admin/parametros/referencia/escolas',
              titulo: 'Escolas',
              icone: School,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/referencia/seguradoras',
              titulo: 'Seguradoras',
              icone: Building2,
              fase: 1,
            },
          ],
        },
        {
          // Ícones do protótipo (`parametrizacaoNavigation.ts:80-88`): hub e
          // "Requisitos Documentais" usam FileCheck, não FileText/ScrollText.
          caminho: '/admin/parametros/documentos',
          titulo: 'Documentos & Compliance',
          icone: FileCheck,
          fase: 1,
          filhos: [
            {
              caminho: '/admin/parametros/documentos/requisitos',
              titulo: 'Requisitos documentais',
              icone: FileCheck,
              fase: 1,
            },
            {
              /**
               * Aponta para a tela de domínio que já existe em Cadastros, não
               * para uma cópia. `documentos/cursos` era a MESMA tela —
               * mesma chave de dados, mesmas colunas, só outro endereço e outro
               * título. A duplicata foi removida de `telas.tsx`.
               *
               * Decisão do dono, 2026-09-04, seguindo o protótipo: o sub-hub de
               * documentos referencia o cadastro de cursos, não duplica.
               */
              caminho: '/admin/parametros/cadastros/cursos-obrigatorios',
              titulo: 'Cursos Obrigatórios',
              icone: GraduationCap,
              fase: 1,
            },
            {
              // Ícone do protótipo: ShieldCheck, não Shield.
              caminho: '/admin/parametros/documentos/apolices',
              titulo: 'Apólices por tipo de veículo',
              icone: ShieldCheck,
              fase: 1,
            },
          ],
        },
        {
          // Ícone do protótipo (`parametrizacaoNavigation.ts:94`): DollarSign.
          caminho: '/admin/parametros/financeiro',
          titulo: 'Financeiro & Tributário',
          icone: DollarSign,
          fase: 1,
          filhos: [
            /*
             * Uma entrada por ABA da Tabela de Preços Contratuais, com o título
             * IDÊNTICO ao da aba de destino (`TabelaDePrecosPage.tsx`).
             *
             * Antes eram duas — "Tabela de Preços" e "Retenções e Faixas IR" —, e
             * nenhuma casava com o nome da aba onde o clique parava; a segunda
             * prometia as faixas de IR e abria a de retenções, que fica numa aba
             * ao lado. Agora origem e destino se chamam do mesmo jeito, e cada aba
             * está a um clique.
             *
             * Cross-link pra área Operação: a tela real vive lá, e `areaReal`
             * evita gerar rota duplicada (ver `App.tsx:rotasDaArea`). A aba vem
             * na query, que é como a própria página a lê — rota não muda.
             *
             * O `resumo` não é só o texto do cartão: a busca global casa por
             * `titulo + resumo` (`BuscaGlobal.tsx`), então é nele que ficam as
             * palavras-chave alternativas (INSS, ISS, IRRF), nunca no título.
             */
            {
              // Aba padrão (`ABA_PADRAO`), por isso sem query: este é o caminho
              // canônico da tela, e é ele que a referência do protótipo declara.
              caminho: '/operacao/financeiro/tabela-precos',
              titulo: 'Componentes da Remuneração',
              icone: TableProperties,
              fase: 1,
              areaReal: '/operacao',
              resumo:
                'Valor fixo, per capita, KM adicional e atividade complementar por tipo de bancada.',
            },
            {
              caminho: '/operacao/financeiro/tabela-precos?aba=retencoes',
              titulo: 'Parâmetros de Retenções',
              icone: Calculator,
              fase: 1,
              areaReal: '/operacao',
              resumo: 'Alíquotas e bases de retenção de INSS e ISS por natureza do credenciado.',
            },
            {
              caminho: '/operacao/financeiro/tabela-precos?aba=ir',
              titulo: 'Faixas Progressivas do IR',
              icone: Percent,
              fase: 1,
              areaReal: '/operacao',
              resumo: 'Faixas, alíquotas e parcela a deduzir do IRRF sobre a remuneração.',
            },
            {
              // Ordem e ícone do protótipo (`parametrizacaoNavigation.ts:99`):
              // vinha solto fora de hub nenhum; ícone era `Gavel`, protótipo
              // usa `AlertTriangle`.
              caminho: '/admin/parametros/tipos-multa',
              titulo: 'Tipos de multa',
              icone: AlertTriangle,
              fase: 1,
              resumo: 'Infrações puníveis, percentual e base de cálculo de cada uma.',
            },
            {
              // Ícone do protótipo: Scale, não Calculator.
              caminho: '/admin/parametros/financeiro/bases-calculo-multa',
              titulo: 'Bases de Cálculo',
              icone: Scale,
              fase: 1,
            },
            {
              // Ícone do protótipo: Ban, não Receipt.
              caminho: '/admin/parametros/financeiro/tipos-glosa',
              titulo: 'Tipos de glosa',
              icone: Ban,
              fase: 1,
            },
            {
              // Ícone do protótipo: Truck, não Bus.
              caminho: '/admin/parametros/financeiro/capacidade-veiculo',
              titulo: 'Capacidade por veículo',
              icone: Truck,
              fase: 1,
            },
            {
              // Ícone do protótipo: FileCheck, não FileText.
              caminho: '/admin/parametros/financeiro/documentos-pagamento',
              titulo: 'Documentos de pagamento',
              icone: FileCheck,
              fase: 1,
            },
            {
              // Rótulo e ícone do protótipo: "Validação do Backfill de
              // Documentos", ícone FileCheck. O endereço fica como está —
              // mesma tela, rota já registrada como aceita em
              // conformidadeComOPrototipo.spec.ts (DIVERGENCIAS_ACEITAS).
              caminho: '/admin/parametros/financeiro/validacao-documentos',
              titulo: 'Validação do Backfill de Documentos',
              icone: FileCheck,
              fase: 1,
            },
            {
              // Ordem do protótipo: último item de Financeiro & Tributário.
              // Vinha solto fora de hub nenhum.
              caminho: '/admin/parametros/precos-publicos',
              titulo: 'Preços públicos',
              icone: Tag,
              fase: 1,
              resumo: 'Valores dos serviços administrativos, com vigência e decreto.',
            },
          ],
        },
        {
          caminho: '/admin/parametros/financeiro/reajuste-anual',
          titulo: 'Reajuste anual',
          icone: TrendingUp,
          fase: 8,
          resumo: 'Índice anual (IPCA/IPC-FIPE) aplicado à tabela de preços.',
        },
        {
          caminho: '/admin/parametros/sla-aprovacao-sme',
          titulo: 'SLA de aprovação SME',
          icone: Clock,
          fase: 8,
          resumo: 'Prazo, em dias úteis, para a SME analisar um lote da DRE.',
        },
        {
          // Ícones do protótipo (`parametrizacaoNavigation.ts:108-117`): hub
          // usa Calendar, não Clock3.
          caminho: '/admin/parametros/prazos',
          titulo: 'Prazos & Calendário',
          icone: Calendar,
          fase: 1,
          resumo: 'SLAs, alertas de vencimento e ciclos de inscrição, num só lugar.',
          filhos: [
            {
              caminho: '/admin/parametros/prazos/sistema',
              titulo: 'Prazos do sistema',
              icone: Clock,
              fase: 1,
            },
            {
              // Ícone do protótipo: Calendar, não CalendarCheck (esse é o
              // ícone de "Ciclos de Inscrição", abaixo).
              caminho: '/admin/parametros/dias-letivos',
              titulo: 'Dias letivos',
              icone: Calendar,
              fase: 1,
            },
            {
              // "Ciclos de Inscrição" do protótipo (`parametrizacaoNavigation.ts:116`,
              // url `/admin/ciclos-inscricao`). Vinha registrada como
              // `/admin/chamamentos` / "Chamamentos e ciclos", solta fora de
              // hub nenhum — corrigido título e rota para bater com o
              // protótipo, mesmo padrão de prefixo `/admin/parametros/` já
              // usado pelos outros itens deste hub.
              caminho: '/admin/parametros/ciclos-inscricao',
              titulo: 'Ciclos de Inscrição',
              icone: CalendarCheck,
              fase: 1,
              resumo: 'Período, edital, vagas por DRE e modalidade, e encerramento das inscrições.',
            },
          ],
        },
        {
          caminho: '/admin/parametros/workflows',
          titulo: 'Workflows & Aprovações',
          icone: Workflow,
          fase: 1,
          resumo: 'Tipos de solicitação, alçadas por valor e parâmetros DTEG.',
          // Ordem e ícones do protótipo (`parametrizacaoNavigation.ts:119-128`).
          // "Tipos de solicitação" e "Parâmetros de aprovação" vinham soltos
          // fora do hub; o primeiro estava fase 6 mesmo tendo tela real
          // construída (TiposSolicitacaoPage).
          filhos: [
            {
              caminho: '/admin/tipos-solicitacao',
              titulo: 'Tipos de solicitação',
              icone: Workflow,
              fase: 1,
              resumo: 'Objetos, filas, prazos e alçadas vigentes.',
            },
            {
              // Ícone do protótipo: DollarSign, não Scale.
              caminho: '/admin/parametros/workflows/alcadas',
              titulo: 'Alçadas por valor',
              icone: DollarSign,
              fase: 1,
            },
            {
              /**
               * Terceira, como no hub: a tela de Workflows & Aprovações a
               * mostra entre "Alçadas por valor" e "Parâmetros de aprovação".
               *
               * Vinha como item solto do grupo Parametrização, marcado de fase
               * 8 — invisível no menu, embora a tela exista desde a ONDA-T1 e a
               * rota esteja registrada. O protótipo não a traz na barra
               * lateral, só como cartão do hub; aqui ela entra nos dois, porque
               * tela sem entrada na navegação é proibida (CLAUDE.md,
               * "Proibido"). O acréscimo está registrado em
               * conformidadeComOPrototipo.spec.ts.
               */
              caminho: '/admin/parametros/workflows/alcadas-genericas',
              titulo: 'Motor de Alçadas (genérico)',
              icone: Scale,
              fase: 1,
              resumo: 'Regras de alçada para defesa intempestiva, glosa e substituição.',
            },
            {
              caminho: '/admin/parametros/aprovacoes',
              titulo: 'Parâmetros de aprovação',
              icone: ShieldCheck,
              fase: 1,
              resumo: 'Regras de validação DTEG por tipo de ação do credenciado.',
            },
          ],
        },
        /**
         * ONDA-T4 — assinatura digital e login gov.br.
         *
         * Não confundir com `/admin/integracoes`, que parametriza o
         * provedor de identidade de verdade e conversa com a API. Estas quatro
         * são regra de negócio: que selo o cidadão precisa para entrar, e que
         * nível jurídico cada documento exige para ser assinado.
         */
        {
          /**
           * A cabeça do sub-hub é `govbr-operacoes`, e não `politica-assinatura`
           * — é assim no protótipo, e a ordem dos filhos também sai de lá.
           *
           * "Auditoria" e "Perfis e Acessos" aparecem aqui E nos grupos próprios
           * deles. Não é duplicata de tela: é o MESMO destino, alcançável por
           * dois caminhos de menu. Decisão do dono em 2026-09-04, seguindo o
           * protótipo — aponta para a tela que já existe, não constrói outra.
           */
          caminho: '/admin/parametros/govbr-operacoes',
          titulo: 'Governança & Assinaturas',
          icone: ShieldCheck,
          fase: 1,
          resumo: 'Selo de login, nível jurídico por documento, provedores aceitos e o fluxo.',
          // Ícones e item faltante do protótipo (`parametrizacaoNavigation.ts:136-142`).
          filhos: [
            {
              caminho: '/admin/parametros/fluxo-assinatura',
              titulo: 'Como funciona a assinatura',
              icone: GitBranch,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/govbr-operacoes',
              titulo: 'Operações Gov.BR',
              icone: ShieldCheck,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/politica-assinatura',
              titulo: 'Política de Assinatura Digital',
              icone: Scale,
              fase: 1,
            },
            {
              caminho: '/admin/parametros/provedores-assinatura',
              titulo: 'Provedores de Assinatura',
              icone: KeyRound,
              fase: 1,
            },
            {
              caminho: '/admin/auditoria',
              titulo: 'Auditoria',
              icone: ShieldCheck,
              fase: 1,
            },
            {
              // Faltava: cross-link igual a "Auditoria" e "Perfis e Acessos",
              // mesmo destino que já existe em Documentos & Compliance.
              caminho: '/admin/parametros/documentos/requisitos',
              titulo: 'Requisitos Documentais',
              icone: FileCheck,
              fase: 1,
            },
            {
              caminho: '/admin/perfis',
              titulo: 'Perfis e Acessos',
              icone: KeyRound,
              fase: 1,
            },
          ],
        },
        // ONDA-T3 — parametrizações de negócio que não pertencem a nenhum hub.
        {
          caminho: '/admin/parametros/precos-publicos',
          titulo: 'Preços públicos',
          icone: Tag,
          fase: 1,
          resumo: 'Valores dos serviços administrativos, com vigência e decreto.',
        },
        {
          caminho: '/admin/parametros/tipos-multa',
          titulo: 'Tipos de multa',
          icone: Gavel,
          fase: 1,
          resumo: 'Infrações puníveis, percentual e base de cálculo de cada uma.',
        },
        /*
         * `/admin/comunicados` NÃO entra aqui. Ele aparecia duas vezes na
         * barra: neste grupo, como "Comunicados — módulo da fase 8", e em
         * "Comunicação", como "Comunicados Oficiais". `itensDaArea` deduplica
         * pelo PRIMEIRO, então o item que virava rota era este — grupo errado
         * e rótulo com sufixo de fase. O documento da atividade
         * (TEG-BO-D, passo de P04) manda abrir a tela "pelo menu
         * (Comunicação)", e é lá que o protótipo a põe (COMUNICAÇÃO >
         * Comunicados Oficiais, ver `referenciaDoPrototipo.ts`). A entrada
         * válida é a de mapa.ts, grupo "Comunicação".
         */
        {
          caminho: '/admin/relatorios',
          titulo: 'Relatórios oficiais',
          icone: BookOpen,
          fase: 8,
          resumo: 'Relatórios oficiais e painéis executivos.',
        },
        {
          caminho: '/admin/migracao/fontes',
          titulo: 'Fontes para migração',
          icone: FileArchive,
          fase: 8,
          resumo: 'Inventário versionado de fontes autorizadas, imutáveis e classificadas.',
        },
        {
          caminho: '/admin/migracao/mapeamento',
          titulo: 'Mapeamento e transformação',
          icone: GitPullRequestArrow,
          fase: 8,
          resumo: 'Correspondências, normalizações, lotes isolados, linhagem e erros.',
        },
        {
          caminho: '/admin/relatorios/rastreabilidade',
          titulo: 'Indicador e rastreabilidade',
          icone: BookOpen,
          fase: 8,
          resumo: 'Totais reproduzíveis ligados a eventos, versões e documentos de origem.',
        },
        {
          caminho: '/admin/relatorios/exportar',
          titulo: 'Exportar documento verificável',
          icone: FileCheck2,
          fase: 8,
          resumo: 'Emissão, download, revogação e código público de autenticidade.',
        },
      ],
    },
    {
      // O grupo "Auditoria" existia só para hospedar um item; no protótipo essa
      // tela mora em Governança. Movida para lá, e o grupo deixou de existir.
      titulo: 'Comunicação',
      itens: [
        {
          caminho: '/admin/comunicados',
          titulo: 'Comunicados Oficiais',
          icone: Megaphone,
          fase: 1,
        },
      ],
    },
    {
      /**
       * ONDA-T1 — configurações operacionais, num hub próprio.
       *
       * No protótipo, "Configurações" é grupo de menu irmão de "Parametrização",
       * não filho (ver `AppSidebar.tsx`: `label: 'Configurações'` é seção
       * própria). Decisão do dono, 2026-08-31: seguir o protótipo à risca aqui.
       * As 4 telas de assinatura digital (ONDA-T4) e as 5 de parametrização de
       * negócio (ONDA-T3) continuam sob "Parametrização", porque é lá que o
       * protótipo as põe (`AppSidebar.tsx`, `parametrizacaoSidebarItems`).
       */
      /**
       * Sete itens flat, igual ao protótipo (`AppSidebar.tsx:162-171`,
       * `label: 'Configurações'`): Configurações, Configurações Globais,
       * Integrações, E-mails, Templates de Notificação, Busca Global,
       * Página 404.
       *
       * Achado em 2026-09-06: o item "Configurações" (`/admin/configuracoes`)
       * não é submenu — no protótipo é uma PÁGINA HUB (`ConfiguracoesHub.tsx`),
       * com cards clicáveis dentro dela, mesmo padrão de `DocumentosHub` e
       * `FinanceiroHub`. As sete telas ONDA-T3 (Prazo de fechamento, Alerta de
       * desvio, Parâmetros do ateste, SLAs institucionais, Matriz de ações em
       * lote, Trabalhos agendados, Configurações gerais) são os cards de
       * dentro dessa página, não item de menu — estão em
       * `ROTAS_FORA_DO_MENU`, alcançáveis por endereço direto até a página hub
       * existir de verdade (pendência registrada, não construída ainda).
       *
       * Revisto em 2026-09-08 (aderência TEG-BO-D): o item "Configurações" já
       * é plano, sem `filhos` — não é Collapsible, então tem link direto na
       * barra e não é rota órfã. A ata do bloco (P03 e P05) diz para abrir
       * E-mails e Templates de Notificação "pelo menu (Comunicação)", mas o
       * próprio `AppSidebar.tsx` do protótipo desmente: `label: 'Comunicação'`
       * tem um item só (Comunicados Oficiais); as duas telas estão em
       * `label: 'Configurações'`, junto com as outras cinco daqui.
       */
      titulo: 'Configurações',
      itens: [
        {
          caminho: '/admin/configuracoes',
          titulo: 'Configurações',
          icone: Settings2,
          fase: 1,
          resumo: 'Hub com as telas de prazo de fechamento, alertas, SLAs e ações em lote.',
        },
        {
          // No protótipo este item leva a `/admin/configuracoes/globais`; aqui
          // a mesma tela nasceu em `/gerais` (ONDA-T1), e a divergência de
          // endereço já está aceita em `conformidadeComOPrototipo.spec.ts`. O
          // item apontava para `/globais`, que não tem tela, e caía no módulo
          // em construção com a tela pronta ao lado. O rótulo segue o protótipo.
          caminho: '/admin/configuracoes/gerais',
          titulo: 'Configurações Globais',
          icone: SlidersHorizontal,
          fase: 1,
          resumo: 'Identidade visual do portal, metadados de busca e limites da inscrição.',
        },
        {
          caminho: '/admin/integracoes',
          titulo: 'Integrações',
          icone: Plug,
          fase: 1,
          resumo: 'Painel do conector de login gov.br e demais sistemas externos.',
        },
        // E-mails e Templates de Notificação vinham do grupo Comunicação. No
        // protótipo os dois vivem em Configurações — Comunicação hospeda só os
        // comunicados oficiais.
        {
          caminho: '/admin/emails',
          titulo: 'E-mails',
          icone: Mail,
          fase: 1,
          resumo: 'Monitoramento e templates de e-mails disparados pelo sistema.',
        },
        {
          caminho: '/admin/notificacoes/templates',
          titulo: 'Templates de Notificação',
          icone: BellRing,
          fase: 1,
          resumo: 'Títulos e mensagens por e-mail, SMS, push e in-app.',
        },
        {
          caminho: '/admin/busca',
          titulo: 'Busca Global',
          icone: Search,
          fase: 1,
          resumo: 'Configuração da busca global do sistema (Cmd+K).',
        },
        {
          caminho: '/admin/pagina-404',
          titulo: 'Página 404',
          icone: AlertTriangle,
          fase: 1,
          resumo: 'Textos e links de navegação exibidos na página de erro 404.',
        },
      ],
    },
    {
      titulo: 'Documentação',
      itens: [
        {
          caminho: '/admin/docs',
          titulo: 'Documentação',
          icone: BookOpen,
          fase: 1,
        },
        {
          caminho: '/admin/design-system',
          titulo: 'Design System',
          icone: Palette,
          fase: 1,
        },
      ],
    },
  ],
};

/**
 * SME e DRE (PRD, seção 4: áreas internas, junto de DTP e administração).
 *
 * Sem grupo de módulo: `docs/PLANO-DE-FASES.md`, fase 7, diz só "as telas de
 * SME e diretorias regionais", sem citar nenhum módulo — diferente das fases
 * 2 a 6 e 8, que nomeiam o que vira item (veículos, rotas, ocorrências...).
 * Inventar módulo aqui seria repetir o erro que a fase 1 já evitou: copiar a
 * arquitetura de informação do protótipo sem validação do cliente. O raiz
 * existe (H-102.A2: o shell precisa servir as quatro áreas internas), o
 * conteúdo vem numa história futura, com o plano de fases atualizado.
 */
/**
 * SME e DRE, unificadas — igual ao protótipo. `AppSidebar.tsx` (o menu comum
 * de Operação/Admin) não tem `/dre` nenhum; o protótipo usa um layout próprio
 * pra este par (`SmeLayout.tsx`, `navGroups`), com um único `/sme/*`
 * compartilhado pelos dois papéis, e o guarda de rota (`ProtectedSmeRoute.tsx`)
 * libera `OPERADOR_SME`, `GESTOR_SME`, `OPERADOR_DRE` e `GESTOR_DRE` juntos.
 *
 * A área e os 5 grupos abaixo são a tradução exata desses `navGroups`: mesmo
 * título, mesma ordem, mesmo ícone. O que muda por papel dentro da mesma área
 * (`smeOnly`, `gestorOnly`, `dreLabel`) virou `ItemDeNavegacao.papeis` e
 * `tituloParaDre` — não existiam antes desta unificação porque, até então,
 * nenhuma área do mapa precisava filtrar item por papel dentro dela mesma.
 *
 * Os itens que já têm tela real hoje foram construídos em
 * `/operacao/financeiro/*` (onda do fechamento mensal/SOF, PR #71) e entram
 * aqui como cross-link (`areaReal`), igual ao já feito em Financeiro &
 * Tributário: "Aprovações" → `AprovacaoSmePage`, "Resumo para Aprovação" →
 * `ResumoAprovacaoSmePage`, "Solicitações" → `SolicitacoesSmeListPage`,
 * "Fechamento" → `FechamentoMensalV2ListPage`, "Relatórios" →
 * `PainelExecutivoPage`, "Auditoria do sistema" → `AuditoriaFinanceiraPage`.
 */
export const AREA_SME: AreaDeNavegacao = {
  raiz: '/sme',
  nome: 'SME',
  icone: Building2,
  papeis: PAPEIS_SME,
  grupos: [
    {
      titulo: 'Início',
      itens: [
        {
          // Entrada da área, igual a `/operacao` e `/admin`, e com tela própria
          // desde a migração do painel do protótipo (`modulos/sme/painel/`,
          // registrada em `TELAS_PRONTAS` por `AREA_SME.raiz`).
          caminho: '/sme',
          titulo: 'Dashboard',
          icone: LayoutDashboard,
          fase: 1,
          resumo: 'Painel da Secretaria Municipal de Educação.',
        },
      ],
    },
    {
      titulo: 'Operação diária',
      itens: [
        {
          // Rota própria de SME (`AprovacaoSmePage`, `src/modulos/financeiro/`),
          // não cross-link: o protótipo registra ESTA MESMA tela duas vezes,
          // em endereços diferentes, cada um dentro do casco visual da área
          // de origem (`/financeiro/aprovacao-sme` dentro do casco de
          // Operação/DTP, `/sme/apontamentos` dentro do casco de SME —
          // `AppPageWrapper` vs `SmePageWrapper`). Usar `areaReal` aqui
          // levava quem clicava no menu de SME pra fora do casco de SME.
          // A cópia em `/operacao/financeiro/aprovacao-sme` continua existindo,
          // fase 5 (invisível lá — o protótipo não lista esse item no
          // Financeiro do DTP, só no menu de SME).
          caminho: '/sme/apontamentos',
          titulo: 'Aprovações',
          tituloParaDre: 'Apontamentos',
          icone: ClipboardCheck,
          fase: 1,
          resumo: 'Fila de aprovação dos apontamentos enviados pelas DREs.',
        },
        {
          // Mesmo caso de "Aprovações": rota própria de SME, não cross-link,
          // pra manter o casco visual de SME ao navegar. A cópia em
          // `/operacao/financeiro/resumo-aprovacao-sme` continua existindo,
          // real e visível no Financeiro do DTP (protótipo lista os dois).
          caminho: '/sme/resumo-aprovacao',
          titulo: 'Resumo para Aprovação',
          icone: FileSpreadsheet,
          fase: 1,
          resumo: 'Resumo consolidado do lote antes da aprovação final.',
        },
        {
          // Tela própria de SME (`TratativasSmeDrePage`,
          // `src/modulos/financeiro/tratativas/`), migrada do protótipo
          // (`SmeTratativasDre.tsx`). A rota `/sme/tratativas-dre` do protótipo
          // era redirecionamento para esta e não foi trazida: rota que só
          // redireciona é rota órfã de tela.
          caminho: '/sme/tratativas',
          titulo: 'Tratativas SME ↔ DRE',
          icone: Scale,
          fase: 1,
          resumo: 'Troca de justificativas entre SME e DRE sobre um apontamento.',
        },
        {
          // Mesmo caso de "Aprovações": rota própria de SME, não cross-link.
          // Aqui `SolicitacoesSmeListPage` monta a variante de mão dupla
          // (DTP ↔ SME, com filtro de origem), como `/sme/demandas` no
          // protótipo; em `/operacao/financeiro/solicitacoes-sme` ela recebe
          // `origem="DTP"` e mostra só DTP → SME. Lista e detalhe ficam na
          // casca em que o usuário entrou.
          caminho: '/sme/demandas',
          titulo: 'Solicitações',
          icone: Send,
          fase: 1,
          papeis: PAPEIS_SOMENTE_SME,
          resumo: 'Pedidos abertos pela SME à equipe DTP.',
        },
      ],
    },
    {
      titulo: 'Ciclo mensal',
      itens: [
        {
          // Rota própria de SME (`FechamentoMensalV2ListPage`), não
          // cross-link, mesmo motivo dos itens acima: o protótipo registra
          // esta tela duas vezes (`/financeiro-v2/fechamento-mensal` no
          // casco de Operação/DTP, `/sme/fechamento-mensal` no casco de SME).
          // A cópia em `/operacao/financeiro/fechamento-mensal` continua
          // existindo, real e visível no Financeiro do DTP.
          caminho: '/sme/fechamento-mensal',
          titulo: 'Fechamento',
          icone: CalendarCheck,
          fase: 1,
          resumo: 'Fechamento da competência: medições, ajustes e demonstrativos.',
        },
        {
          // Tela própria (`RevisoesSmeListPage`), migrada do protótipo em
          // 2026-09-22 — leitura mockada, decisão ainda não grava.
          caminho: '/sme/revisoes',
          titulo: 'Pedidos de revisão',
          icone: Scale,
          fase: 1,
          papeis: PAPEIS_SOMENTE_SME,
          resumo: 'Pedidos de revisão abertos pelos credenciados.',
        },
        {
          // NÃO é cross-link pro "Painel executivo" de Operação — são telas
          // diferentes. Esta é relatório por DRE × competência (alunos,
          // valores, prazo de aprovação, devoluções, com tabela e exportação
          // CSV); "Indicadores Mensais", que existe aqui, mede outra coisa
          // (regime PF×PJ) e já é item próprio do protótipo noutro lugar do
          // menu. Tela própria desde a migração do protótipo
          // (`RelatoriosSmePage`), registrada em App.tsx só sob /sme.
          caminho: '/sme/relatorios',
          titulo: 'Relatórios',
          icone: BarChart3,
          fase: 1,
          // Quatro papéis, como o guard do protótipo (ver
          // PAPEIS_RELATORIOS_SME): gestor de DRE inclusive.
          papeis: PAPEIS_RELATORIOS_SME,
          resumo:
            'Relatório gerencial por DRE e competência: alunos, valores, prazos e devoluções.',
        },
      ],
    },
    {
      titulo: 'Acompanhamento',
      itens: [
        {
          // Tela própria: `CoberturaPorDrePage` (modulos/sme/cobertura).
          caminho: '/sme/cobertura',
          titulo: 'Cobertura',
          icone: Map,
          fase: 1,
          papeis: PAPEIS_SOMENTE_SME,
          resumo: 'Escolas, OS em execução e alunos atendidos por DRE.',
        },
        {
          // Rota própria de SME (`AuditoriaFinanceiraPage`), não cross-link,
          // mesmo motivo dos itens acima. A cópia em
          // `/operacao/financeiro/auditoria` continua existindo, real e
          // visível no Financeiro do DTP.
          caminho: '/sme/auditoria',
          titulo: 'Auditoria do sistema',
          icone: History,
          fase: 1,
          papeis: PAPEIS_SOMENTE_GESTOR_SME,
          resumo: 'Trilha de auditoria dos lançamentos financeiros.',
        },
      ],
    },
    {
      titulo: 'Comunicação',
      itens: [
        {
          // Tela própria: `MensagensSmePage` (modulos/sme/mensagens). Visível
          // também à DRE, como no protótipo; a tela explica por que a caixa
          // ainda não é servida a quem é só DRE.
          caminho: '/sme/mensagens',
          titulo: 'Mensagens',
          icone: MessagesSquare,
          fase: 1,
          resumo: 'Mensagens trocadas com credenciados e DREs.',
        },
        {
          // Mesma tela de `/operacao/notificacoes`, montada na casca de SME
          // (modulos/sme/notificacoes), como o protótipo faz.
          caminho: '/sme/notificacoes',
          titulo: 'Notificações',
          icone: Bell,
          fase: 1,
          resumo: 'Central de notificações da SME.',
        },
      ],
    },
  ],
};

/**
 * Portal do credenciado — autoatendimento do transportador (PRD, seção 4;
 * docs/PLANO-DE-FASES.md, fase 7).
 *
 * ENTREGUE FORA DA ORDEM DO PLANO DE FASES, POR DECISÃO REGISTRADA. O plano
 * lista esta área na fase 7, depois do credenciamento, da frota e da apuração
 * (fases 2 a 6) — porque na ordem normal o portal exibiria dado real dessas
 * fases. O time decidiu adiantar a interface, com dado mockado no lugar da API
 * real (ainda inexistente para estas telas), para portar o protótipo antes do
 * backend estar pronto — mesma lógica já usada em `operacao/condutores`
 * (`comum/cadastroIndisponivel.ts`), mas aqui a leitura também é mockada, não
 * só a escrita. `fase: 1` porque a tela existe hoje, de fato — não é o módulo
 * em construção. Trocar o mock pela API real é dívida técnica registrada, não
 * ausência de tela.
 */
export const AREA_CREDENCIADO: AreaDeNavegacao = {
  raiz: '/credenciado',
  nome: 'Portal do Credenciado',
  icone: Truck,
  // Só os papéis externos: o Portal é de quem é a própria transportadora, e
  // nenhum papel interno entra nele (decisão do dono em 2026-10-02). A API
  // recusa do mesmo jeito; quem confere o portal em DSV usa a conta do
  // workflow "Criar credenciado de teste (DSV)". Travado por
  // `papeisDoPortalCredenciado.spec.tsx`.
  papeis: PAPEIS_EXTERNOS,
  atalhosRapidos: [
    '/credenciado/financeiro',
    '/credenciado/solicitacoes',
    '/credenciado/documentos',
  ],
  grupos: [
    /**
     * ESTA ÁREA SEGUE A REFERÊNCIA DO PROTÓTIPO ITEM A ITEM.
     *
     * Reconstruída na fase 3 da reconciliação de navegação, primeira das cinco
     * áreas — era a mais fiel de todas, com cinco divergências, e por isso serve
     * para validar o método antes das áreas grandes.
     *
     * Grupo, rótulo e ORDEM saem de `referenciaDoPrototipo.ts`. Antes de mudar
     * qualquer coisa aqui, mude lá — o teste de conformidade compara os dois e
     * acusa a diferença.
     */
    {
      titulo: 'Início',
      itens: [{ caminho: '/credenciado', titulo: 'Dashboard', icone: Home, fase: 1 }],
    },
    {
      titulo: 'Meu Cadastro',
      itens: [
        {
          // Os mesmos papéis que a API aceita
          // (`portal-credenciado-inscricao.controller.ts`, `@Papeis`): o
          // FINANCEIRO_CREDENCIADO não lê a inscrição, e item que abre numa
          // recusa não é renderizado (regra 7 de interface). No protótipo todo
          // usuário do portal vê o item; a restrição veio com a API.
          caminho: '/credenciado/inscricao',
          titulo: 'Minha Inscrição',
          icone: IdCard,
          fase: 1,
          papeis: ['CREDENCIADO', 'ADMIN_CREDENCIADO', 'OPERADOR_CREDENCIADO'],
        },
        {
          /**
           * Documentação do pré-credenciamento (frente F10, D-28): o
           * contemplado no sorteio envia os documentos da empresa ou pessoa,
           * do veículo, do condutor e do monitor. Só aparece para o
           * pré-credenciado; não existe no protótipo — acréscimo registrado em
           * `conformidadeComOPrototipo.spec.ts`. O dossiê de um veículo,
           * `/credenciado/pre-credenciamento/:id`, é sub-rota da própria tela.
           */
          caminho: '/credenciado/pre-credenciamento',
          titulo: 'Documentação do pré-credenciamento',
          icone: FileCheck,
          fase: 1,
          papeis: ['CREDENCIADO'],
          somentePreCredenciado: true,
        },
        {
          // Só quem administra o credenciado coletivo vê este item: o titular
          // PJ ou Cooperativa (`CREDENCIADO`) e o administrador convidado
          // (`ADMIN_CREDENCIADO`); PF não tem equipe. Decisão do dono em
          // 2026-10-01 — no protótipo o titular só recebe `CREDENCIADO` e o
          // item ficava só para `isAdminCred || isAdmin` (`PortalLayout.tsx`,
          // `buildPortalMenu`). Mesma regra da tela, `usePortalAccess`.
          // Papel interno não entra no Portal (`papeis` da área, acima), e
          // por isso também não está aqui.
          caminho: '/credenciado/equipe',
          titulo: 'Minha Equipe',
          icone: Users,
          fase: 1,
          papeis: ['CREDENCIADO', 'ADMIN_CREDENCIADO'],
          condicionalPorTipo: 'coletivo',
          guardaNaTela: true,
        },
        {
          // Os mesmos papéis que a API aceita
          // (`portal-credenciado-documentos.controller.ts`, `@Papeis`), como em
          // Minha Inscrição: o FINANCEIRO_CREDENCIADO receberia 403, e item que
          // abre numa recusa não é renderizado (regra 7 de interface).
          caminho: '/credenciado/documentos',
          titulo: 'Documentos',
          icone: FileText,
          fase: 1,
          papeis: ['CREDENCIADO', 'ADMIN_CREDENCIADO', 'OPERADOR_CREDENCIADO'],
        },
      ],
    },
    {
      titulo: 'Operação',
      itens: [
        {
          caminho: '/credenciado/frota-equipe',
          titulo: 'Frota & Equipe',
          icone: Bus,
          fase: 1,
        },
        {
          caminho: '/credenciado/cooperativa/inscrever-terceiro',
          titulo: 'Inscrever Terceiros',
          icone: UserPlus,
          fase: 1,
          condicionalPorTipo: 'cooperativa',
        },
        {
          caminho: '/credenciado/veiculos',
          titulo: 'Meus Veículos',
          icone: Car,
          fase: 1,
        },
        {
          caminho: '/credenciado/monitores',
          titulo: 'Meus Monitores',
          icone: UserRound,
          fase: 1,
        },
        {
          caminho: '/credenciado/condutores',
          titulo: 'Meus Condutores',
          icone: UserCheck,
          fase: 1,
          condicionalPorTipo: 'coletivo',
        },
        {
          caminho: '/credenciado/cooperados',
          titulo: 'Meus Cooperados',
          icone: Users,
          fase: 1,
          condicionalPorTipo: 'cooperativa',
        },
        {
          caminho: '/credenciado/contratos',
          titulo: 'Contratos e Termos',
          icone: FileText,
          fase: 1,
        },
      ],
    },
    {
      titulo: 'Financeiro',
      itens: [
        {
          caminho: '/credenciado/financeiro',
          titulo: 'Demonstrativos',
          icone: Wallet,
          fase: 1,
        },
        {
          caminho: '/credenciado/extrato',
          titulo: 'Extrato Consolidado',
          icone: Receipt,
          fase: 1,
        },
        {
          caminho: '/credenciado/cobrancas',
          titulo: 'Cobranças',
          icone: CreditCard,
          fase: 1,
        },
        {
          caminho: '/credenciado/recursos',
          titulo: 'Recursos',
          icone: Gavel,
          fase: 1,
        },
      ],
    },
    {
      titulo: 'Atendimento',
      itens: [
        {
          caminho: '/credenciado/solicitacoes',
          titulo: 'Solicitações',
          icone: ClipboardList,
          fase: 1,
        },
      ],
    },
    {
      // Grupo próprio, como no protótipo. Antes o item vivia dentro de
      // Atendimento, e o agrupamento se perdia.
      titulo: 'Notificações',
      itens: [
        {
          caminho: '/credenciado/notificacoes',
          titulo: 'Notificações',
          icone: Bell,
          fase: 1,
        },
      ],
    },
  ],
};

export const AREAS: readonly AreaDeNavegacao[] = [
  AREA_OPERACAO,
  AREA_ADMIN,
  AREA_SME,
  AREA_CREDENCIADO,
];

/**
 * Todos os itens de uma área, sem os grupos — inclusive os filhos de item com
 * submenu, cada um contando como destino próprio (rota, título, teste de rota
 * órfã). Sem isso, um filho nunca vira <Route> em App.tsx e cai em "não
 * encontrado" ao navegar — rota órfã, proibida por CLAUDE.md.
 *
 * Deduplicado por caminho: um item com filhos que não tem tela própria costuma
 * repetir o caminho do primeiro filho (ex.: "Termos" e "Todos os termos" levam
 * ao mesmo lugar) — sem isso, os dois virariam <Route> duplicada. Em caso de
 * empate, o item pai vence (aparece primeiro no achatamento).
 */
export function itensDaArea(area: AreaDeNavegacao): ItemDeNavegacao[] {
  const achatado = area.grupos.flatMap((grupo) =>
    grupo.itens.flatMap((item) => [item, ...(item.filhos ?? [])]),
  );
  const vistos = new Set<string>();
  return achatado.filter((item) => {
    if (vistos.has(item.caminho)) {
      return false;
    }
    vistos.add(item.caminho);
    return true;
  });
}

/** Todos os itens de todas as áreas. */
export function todosOsItens(): ItemDeNavegacao[] {
  return AREAS.flatMap(itensDaArea);
}

/**
 * Os grupos de uma área como o MENU deve mostrá-los: só o que existe hoje.
 *
 * Decisão do PO em 2026-09-05: item de fase futura não aparece no menu. O menu
 * listava telas das fases 3 a 8 — 67 itens de roadmap exposto na navegação, que
 * o usuário clicava para encontrar "módulo em construção".
 *
 * O ROADMAP CONTINUA NO MAPA, e de propósito. Apagar os 67 itens levaria junto o
 * `resumo` de cada um, que é a única descrição escrita do que cada tela será, e
 * a rota que hoje leva ao aviso honesto de "em construção" viraria 404 para quem
 * tem o link. Filtrar na hora de renderizar preserva as duas coisas e se desfaz
 * numa linha, se a decisão mudar.
 *
 * Grupo que fica sem nenhum item visível some junto — cabeçalho de grupo vazio
 * não informa nada.
 *
 * `papeisDoUsuario` filtra por `ItemDeNavegacao.papeis`, quando o item declara
 * essa restrição (SME/DRE, que dividem a mesma área — ver o campo). Sem essa
 * lista, item restrito por papel não aparece: o padrão é esconder, não vazar.
 */
export function gruposVisiveis(
  area: AreaDeNavegacao,
  papeisDoUsuario?: readonly Papel[],
  tipoDeCredenciado?: TipoDeCredenciado | null,
  preCredenciado?: boolean,
): GrupoDeNavegacao[] {
  const isColetivo = tipoDeCredenciado === 'PJ' || tipoDeCredenciado === 'COOPERATIVA';
  const isCoop = tipoDeCredenciado === 'COOPERATIVA';

  const podeVer = (item: ItemDeNavegacao) =>
    (!item.papeis || (papeisDoUsuario?.some((papel) => item.papeis?.includes(papel)) ?? false)) &&
    (!item.condicionalPorTipo || (item.condicionalPorTipo === 'coletivo' ? isColetivo : isCoop)) &&
    (!item.somentePreCredenciado || preCredenciado === true);

  return area.grupos
    .map((grupo) => ({
      ...grupo,
      itens: grupo.itens
        .filter((item) => item.fase === 1 && podeVer(item))
        .map((item) => ({
          ...item,
          filhos: item.filhos?.filter((filho) => filho.fase === 1 && podeVer(filho)),
        })),
    }))
    .filter((grupo) => grupo.itens.length > 0);
}

/**
 * O item `somentePreCredenciado` aparece para quem entra como pré-credenciado
 * e também para o credenciado que já existe e tem dossiê de pré-credenciamento
 * aberto (veículo novo contemplado): os dois vêm do `GET /eu`, derivados do
 * banco. É o valor que `gruposVisiveis` recebe em `preCredenciado`.
 */
export function veDocumentacaoPreCredenciamento(
  usuario?: { preCredenciado?: boolean; documentacaoPreCredenciamento?: boolean } | null,
): boolean {
  return usuario?.preCredenciado === true || usuario?.documentacaoPreCredenciamento === true;
}

/** Uma aparição de item no menu, com o grupo e o pai de onde ela vem. */
export interface OcorrenciaDeItem {
  item: ItemDeNavegacao;
  area: string;
  grupo: string;
  /** Caminho do item pai, quando esta ocorrência é de um filho. */
  pai?: string;
}

/**
 * Todas as aparições de item numa área, SEM DEDUPLICAR.
 *
 * `itensDaArea` deduplica de propósito, para o roteador não criar `<Route>`
 * repetida — e é essa a visão que a aplicação usa. Mas a barra lateral renderiza
 * de `area.grupos` direto: um caminho declarado em dois grupos aparece DUAS
 * VEZES para quem usa o sistema, e a visão deduplicada não enxerga isso.
 *
 * Esta função existe para a conferência ver o menu como o usuário vê. Foi criada
 * depois de o teste "não tem caminho repetido" passar por anos sem poder falhar:
 * ele conferia a lista já deduplicada, o que o tornava tautológico, e deixou
 * passar `/admin/comunicados` declarado em Parametrização e em Comunicação.
 */
export function ocorrenciasDaArea(area: AreaDeNavegacao): OcorrenciaDeItem[] {
  return area.grupos.flatMap((grupo) =>
    grupo.itens.flatMap((item) => [
      { item, area: area.raiz, grupo: grupo.titulo },
      ...(item.filhos ?? []).map((filho) => ({
        item: filho,
        area: area.raiz,
        grupo: grupo.titulo,
        pai: item.caminho,
      })),
    ]),
  );
}

/** Todas as aparições de item, de todas as áreas, sem deduplicar. */
export function todasAsOcorrencias(): OcorrenciaDeItem[] {
  return AREAS.flatMap(ocorrenciasDaArea);
}

/** O item da rota, quando existe. */
export function itemPorCaminho(caminho: string): ItemDeNavegacao | undefined {
  return todosOsItens().find((item) => item.caminho === caminho);
}

/**
 * O item cujo `caminho` é o prefixo mais específico do caminho dado — usado
 * pelo breadcrumb (`Migalhas.tsx`), que precisa achar o item da barra
 * lateral mesmo estando numa sub-rota que o item não declara como filho
 * (aba interna, detalhe). Ex.: com o item "Fiscalização" registrado só em
 * `/operacao/fiscalizacao`, isto acha esse item tanto para
 * `/operacao/fiscalizacao/ocorrencias` quanto para
 * `/operacao/fiscalizacao/ocorrencias/abc123`.
 *
 * Ao contrário de `itemPorCaminho` (casamento exato, usado pela conferência
 * de rota órfã em `mapa.spec.ts` — não trocar essa função por esta ali, o
 * teste precisa do casamento exato pra pegar rota esquecida de verdade),
 * este casa por prefixo de segmento inteiro: "/operacao/fiscalizacaox" não
 * casa com o item "/operacao/fiscalizacao". Em empate de vários itens cujo
 * caminho é prefixo do dado, vence o mais específico (caminho mais longo).
 */
export function itemPorPrefixoDeCaminho(caminho: string): ItemDeNavegacao | undefined {
  const candidatos = todosOsItens().filter(
    (item) => caminho === item.caminho || caminho.startsWith(`${item.caminho}/`),
  );
  candidatos.sort((a, b) => b.caminho.length - a.caminho.length);
  return candidatos[0];
}

/**
 * Se quem tem estes papéis consegue abrir o caminho — o mesmo corte que o
 * roteador faz: o RBAC da área dona do caminho (`AreaDeNavegacao.papeis`, a
 * guarda de `RotaProtegida`) e, quando o item declara, o do próprio item.
 *
 * Existe para link que aponta para outra área. Uma tela montada em mais de uma
 * casca (Notificações, em `/operacao` e `/sme`) aponta para destinos que nem
 * todo papel das duas abre, e o CLAUDE.md, regra 7 de interface, não admite
 * renderizar um link que só leva a "acesso negado".
 *
 * Caminho fora de toda área (portal público, raiz) é aberto a qualquer um.
 */
/**
 * Os papéis que a ROTA de um item exige, além dos da área. É `item.papeis`,
 * salvo quando a tela tem guarda própria (`guardaNaTela`): aí a rota fica
 * aberta à área inteira e a tela mostra o bloqueio do protótipo.
 */
export function papeisDaRota(item: ItemDeNavegacao): readonly Papel[] | undefined {
  return item.guardaNaTela ? undefined : item.papeis;
}

export function papeisAbremCaminho(caminho: string, papeis: readonly Papel[]): boolean {
  const area = AREAS.filter(
    (candidata) => caminho === candidata.raiz || caminho.startsWith(`${candidata.raiz}/`),
  ).sort((a, b) => b.raiz.length - a.raiz.length)[0];
  if (!area) {
    return true;
  }
  if (!area.papeis.some((papel) => papeis.includes(papel))) {
    return false;
  }
  const item = itemPorPrefixoDeCaminho(caminho);
  return !item?.papeis || item.papeis.some((papel) => papeis.includes(papel));
}

/**
 * O grupo de menu em que a rota mora — o segmento do meio do breadcrumb.
 *
 * A trilha mostrava só "casa › tela", perdendo o módulo do caminho: quem
 * abrisse `/admin/governanca/auditoria` lia "Auditoria" sem saber que estava
 * em Governança. O protótipo mostra o módulo ("Admin › Auditoria"), e o pacote
 * de QA registrou a perda em seis fichas.
 *
 * Casa item de primeiro nível e filho de submenu, e devolve o título do grupo.
 * Devolve `undefined` quando o grupo repetiria o que já está na trilha: a raiz
 * da área (o ícone de casa já leva lá) e o grupo homônimo do item, como o
 * grupo "Auditoria" cujo único item também se chama "Auditoria".
 */
export function grupoDoItem(caminho: string): string | undefined {
  for (const area of AREAS) {
    if (caminho === area.raiz) {
      return undefined;
    }
    for (const grupo of area.grupos) {
      for (const item of grupo.itens) {
        const ehEste =
          item.caminho === caminho || (item.filhos ?? []).some((f) => f.caminho === caminho);
        if (!ehEste) {
          continue;
        }
        return grupo.titulo.toLowerCase() === item.titulo.toLowerCase() ? undefined : grupo.titulo;
      }
    }
  }
  return undefined;
}

/**
 * Rotas que existem de propósito FORA do menu, e por que.
 *
 * O teste de rota órfã usa esta lista para distinguir "esqueci de colocar no
 * menu" de "não faz sentido no menu". Cada entrada precisa de motivo escrito —
 * sem isso, a lista viraria despejo para calar o teste.
 */
export const ROTAS_FORA_DO_MENU: ReadonlyArray<{ caminho: string; motivo: string }> = [
  { caminho: '/', motivo: 'Portal público: é a raiz, não item de menu do sistema interno.' },
  { caminho: '/login', motivo: 'Entrada. Quem está dentro não precisa dela no menu.' },
  /*
    "Meu perfil" é destino do MENU DA CONTA, não da barra lateral — igual ao
    protótipo, que também não o põe na lateral. Lá é uma rota só (`/perfil`),
    alcançada de qualquer layout; aqui cada área tem casca própria, então a
    mesma tela é montada uma vez por área, dentro da casca de cada uma
    (decisão do dono em 2026-09-11). O Portal do Credenciado ganhou a dele em
    05/10/2026 (ordem M3), junto com as telas de detalhe do credenciado, mais
    abaixo: até então o "Meu Perfil" do portal levava à página inicial, como no
    protótipo.
  */
  {
    caminho: '/admin/perfil',
    motivo: 'Meu perfil: destino do menu da conta, não item de barra lateral.',
  },
  {
    caminho: '/operacao/perfil',
    motivo: 'Meu perfil: destino do menu da conta, não item de barra lateral.',
  },
  {
    caminho: '/sme/perfil',
    motivo: 'Meu perfil: destino do menu da conta, não item de barra lateral.',
  },
  /*
    "Preferências de notificação", par de "Meu perfil" no mesmo menu da conta.
    No protótipo a preferência é POR USUÁRIO (`notification_preferences.user_id`),
    e o mesmo componente serve área interna e portal. A SME entrou em
    2026-09-24, por decisão do dono, embora o menu de conta dela no protótipo
    (`SmeLayout.tsx`) não tenha o item: a rota nasce junto com a entrada em
    `MenuDaContaSme`, então não é órfã.
  */
  {
    caminho: '/admin/perfil/notificacoes',
    motivo: 'Preferências de notificação: destino do menu da conta, não item de barra lateral.',
  },
  {
    caminho: '/operacao/perfil/notificacoes',
    motivo: 'Preferências de notificação: destino do menu da conta, não item de barra lateral.',
  },
  {
    caminho: '/sme/perfil/notificacoes',
    motivo: 'Preferências de notificação: destino do menu da conta, não item de barra lateral.',
  },
  {
    caminho: '/esqueci-minha-senha',
    motivo: 'Alcançada pela tela de entrada, por quem não tem sessão.',
  },
  {
    caminho: '/redefinir-senha',
    motivo: 'Destino do link enviado por e-mail pelo Keycloak.',
  },
  {
    caminho: '/primeiro-acesso',
    motivo: 'Etapa de entrada para quem nunca acessou. Quem está dentro não precisa dela no menu.',
  },
  {
    caminho: '/privacidade',
    motivo: 'Aviso de privacidade: fica no rodapé do portal e no aviso de cookies, não no menu.',
  },
  /*
   * Portal público. Este mapa descreve o menu do sistema interno; a navegação
   * pública é outra árvore, declarada em `modulos/portal/navegacaoPublica.ts` e
   * conferida contra o protótipo pelo mesmo teste de conformidade.
   */
  {
    caminho: '/portal/:slug',
    motivo:
      'Endereço canônico de qualquer página publicada pelo editor de conteúdo da Administração. Não é item de menu: o menu público cita as páginas institucionais pelo apelido fixo.',
  },
  {
    caminho: '/programa',
    motivo: 'Página institucional do portal público: está no cabeçalho e no rodapé do portal.',
  },
  {
    caminho: '/credenciados',
    motivo: 'Página institucional do portal público: está no cabeçalho e no rodapé do portal.',
  },
  {
    caminho: '/credenciados/acompanhamento',
    motivo:
      'Acompanhamento da inscrição, alcançado pelo rodapé do portal e pela chamada do hero; a consulta por protocolo e fator continua respondendo no endereço de jornada já divulgado.',
  },
  {
    caminho: '/credenciados/acompanhamento/:protocolo',
    motivo:
      'Detalhe de uma inscrição: alcançado pelo cartão da lista de acompanhamento, nunca direto do menu (regra 9 de interface).',
  },
  {
    caminho: '/credenciados/pre-credenciado/acesso',
    motivo:
      'Destino do link de uso único do e-mail de acesso do pré-credenciado (F09): só tem sentido com o token que chega no e-mail.',
  },
  {
    caminho: '/credenciados/autocadastro',
    motivo:
      'Autocadastro do transportador, alcançado pela chamada do hero da página inicial, pelo cartão "Próximo passo" da página Para credenciados e pelos links de quem ainda não tem inscrição.',
  },
  {
    caminho: '/credenciados/entrar',
    motivo:
      'Entrada do candidato, alcançada pelo acompanhamento ("já tem conta no portal?"), pela recuperação de acesso e pelo cartão "Próximo passo" da página Para credenciados ("Retomar rascunho salvo" e "Acessar minha conta").',
  },
  {
    caminho: '/credenciados/recuperar',
    motivo:
      'Recuperação de acesso à inscrição, alcançada pela entrada do candidato e pelo acompanhamento.',
  },
  {
    caminho: '/credenciados/enviada/:protocolo',
    motivo:
      'Comprovante de envio da inscrição: alcançado logo depois do envio e pelo detalhe do acompanhamento.',
  },
  {
    caminho: '/transparencia',
    motivo: 'Página institucional do portal público: abre o submenu de transparência no cabeçalho.',
  },
  {
    caminho: '/dados-publicos',
    motivo: 'Página institucional do portal público: está no submenu de transparência e no rodapé.',
  },
  {
    caminho: '/dados-publicos/credenciados',
    motivo:
      'Consulta pública de credenciados, alcançada pelo rodapé do portal, pela chamada do hero e pelas consultas públicas da página Dados Públicos; a verificação de publicação por código continua respondendo no endereço de jornada já divulgado.',
  },
  {
    caminho: '/dados-publicos/sorteios',
    motivo:
      'Resultado público dos sorteios do credenciamento (D-07): tela do portal público, não da barra lateral. Desde a decisão D1 do dono (2026-09-30) está no submenu Transparência do cabeçalho e no rodapé, coluna Institucional; também é alcançada pelas consultas públicas da página Dados Públicos (D-32), pelo "Todos os sorteios" de cada resultado e pelo painel interno do sorteio.',
  },
  {
    caminho: '/dados-publicos/sorteios/:id',
    motivo:
      'Resultado de um sorteio: alcançado pela linha da lista de sorteios, pelo link do e-mail de resultado (link_resultado) e pelo painel interno — detalhe nunca é item de menu (regra 9 de interface).',
  },
  {
    caminho: '/legislacao',
    motivo: 'Página institucional do portal público: está no submenu de transparência e no rodapé.',
  },
  {
    caminho: '/termos-de-uso',
    motivo: 'Página institucional do portal público: está no rodapé do portal.',
  },
  {
    caminho: '/credenciado/veiculos/:slug',
    motivo:
      'Tela de detalhe de veículo, alcançada pelo clique na linha da listagem — regra de interface 9 do CLAUDE.md diz que não existe botão "Ver", então o endereço não é item de menu próprio.',
  },
  {
    caminho: '/credenciado/monitores/:slug',
    motivo:
      'Tela de detalhe de monitor, alcançada pelo clique na linha da listagem, não pelo menu.',
  },
  {
    caminho: '/credenciado/condutores/:slug',
    motivo:
      'Tela de detalhe de condutor, alcançada pelo clique na linha da listagem, não pelo menu.',
  },
  {
    caminho: '/credenciado/contratos/:id',
    motivo:
      'Tela de detalhe de contrato, alcançada pelo clique na linha da listagem, não pelo menu.',
  },
  {
    caminho: '/credenciado/ordens-de-servico/:id',
    motivo:
      'Detalhe de uma Ordem de Serviço, aberto pela linha da sub-tabela de Contratos e Termos ou da aba OS vinculadas do Termo (regra 9 de interface); não é item de menu, como no protótipo.',
  },
  {
    caminho: '/credenciado/financeiro/:id',
    motivo:
      'Demonstrativo de uma medição específica, alcançado pelo clique na linha da listagem financeira.',
  },
  {
    caminho: '/credenciado/solicitacoes/nova',
    motivo:
      'Ação de criar, não item de menu — mapa.ts já evita isso (ver comentário de atalhosRapidos): o menu aponta para a lista, que já existe, e o botão "Nova solicitação" mora dentro dela.',
  },
  {
    caminho: '/credenciado/solicitacoes/:id',
    motivo:
      'Tela de detalhe de solicitação, alcançada pelo clique na linha da listagem, não pelo menu.',
  },
  {
    caminho: '/credenciado/perfil',
    motivo:
      'Meu perfil do credenciado: destino do menu da conta (avatar), não item de barra lateral — a mesma tela das outras áreas.',
  },
  {
    caminho: '/credenciado/perfil/notificacoes',
    motivo:
      'Preferências de notificação: acessível pelo menu da conta (avatar), não pela barra lateral.',
  },
  {
    caminho: '/acessibilidade',
    motivo: 'Declaração de acessibilidade: também no rodapé do portal.',
  },
  {
    caminho: '/publicacoes',
    motivo:
      'Publicações vigentes do portal público: desde a decisão D2 do dono (2026-09-30), consulta fixa da página Legislação (CONSULTAS_POR_PAGINA), alcançada pelo submenu Transparência e pelo rodapé; saiu do cabeçalho em 2026-09-06, pela fidelidade ao protótipo.',
  },
  {
    caminho: '/verificar-documento',
    motivo:
      'Verificador público do código impresso no documento oficial: desde a decisão D1 do dono (2026-09-30) está no rodapé do portal, coluna Serviços, depois de Acompanhar inscrição.',
  },
  {
    caminho: '/operacao/financeiro/apuracao-mensal/:mes/:dreId',
    motivo:
      'Detalhe da grade de apuração de uma DRE num mês (FIN-061): alcançado clicando numa linha ' +
      'da listagem de apuração, não é destino de menu.',
  },
  {
    caminho: '/operacao/financeiro/apuracao/lancamento/:osId/:mes',
    motivo:
      'Formulário de lançamento de uma OS na competência (FIN-062): alcançado pelo número da OS ' +
      'dentro da grade de apuração, não é destino de menu.',
  },
  {
    caminho: '/operacao/financeiro/medicoes/:id',
    motivo:
      'Detalhe de uma medição (FIN-002), com 9 abas internas: alcançado clicando numa linha da ' +
      'listagem de Medições, não é destino de menu.',
  },
  {
    caminho: '/operacao/financeiro/fechamento-mensal/:id',
    motivo:
      'Detalhe do fechamento mensal de uma competência/natureza (FIN-023), com 6 abas internas: ' +
      'alcançado a partir da listagem de Fechamento mensal, não é destino de menu.',
  },
  {
    caminho: '/sme/fechamento-mensal/:id',
    motivo:
      'Mesmo detalhe de fechamento mensal (FIN-023), dono duplo com Operação — igual ao ' +
      'protótipo (`SmeFechamentoMensalDetail.tsx`, reusa `FechamentoMensalV2DetailPage` dentro ' +
      'do casco de SME). Sem esta rota, a lista redirecionava pra fora do casco de SME ao abrir ' +
      'a competência (mesmo bug 1.6 já corrigido no protótipo com `useFechamentoBasePath`).',
  },
  /*
    Medições e Demonstrativos da SME saíram do menu no protótipo porque as abas
    do "Ciclo mensal" os absorveram (`SmeLayout.tsx`, comentário "M1";
    `CicloMensalTabs.tsx`). A entrada é a faixa de abas, que aparece na lista de
    Fechamento da SME e nas duas telas (`ciclo-mensal-sme/CicloMensalAbas.tsx`).
  */
  {
    caminho: '/sme/medicoes',
    motivo:
      'Medições da SME: aba do Ciclo mensal, alcançada pela faixa de abas do Fechamento ' +
      '(`CicloMensalAbas`), não por item de menu — igual ao protótipo, que a tirou do menu.',
  },
  {
    caminho: '/sme/demonstrativos',
    motivo:
      'Demonstrativos da SME: aba do Ciclo mensal, alcançada pela faixa de abas do Fechamento ' +
      '(`CicloMensalAbas`) e pela linha da aba Medições, não por item de menu.',
  },
  {
    caminho: '/operacao/financeiro/resumo-aprovacao-sme/:competencia',
    motivo:
      'Resumo para Aprovação SME de uma competência escolhida: destino do botão "Resumo ' +
      'institucional" da Aprovação SME, igual ao protótipo (`/financeiro/resumo-aprovacao-sme/' +
      ':competencia`). A competência sem parâmetro já é item de menu.',
  },
  {
    caminho: '/sme/resumo-aprovacao/:competencia',
    motivo:
      'Mesmo resumo da competência escolhida, dentro do casco de SME — o protótipo registra ' +
      '`/sme/resumo-aprovacao/:competencia`. Sem esta rota o botão "Resumo institucional" de ' +
      '`/sme/apontamentos` caía no "não encontrado" e ainda levava para fora do casco de SME.',
  },
  {
    caminho: '/operacao/financeiro/solicitacoes-sme/:id',
    motivo:
      'Detalhe de uma solicitação DTP → SME (FIN-016): alcançado clicando numa linha da listagem, ' +
      'não é destino de menu.',
  },
  {
    caminho: '/sme/demandas/:id',
    motivo:
      'Mesmo detalhe de solicitação, dono duplo com o Financeiro — igual ao protótipo ' +
      '(`/sme/demandas/:id`, `SmeDemandaDetail`). Sem esta rota, a linha da lista de SME ' +
      'levava para `/operacao/...` e o usuário saía do casco de SME.',
  },
  {
    caminho: '/operacao/condutores/:slug',
    motivo: 'Detalhe de condutor alcançado pela linha da listagem operacional, não pelo menu.',
  },
  {
    caminho: '/operacao/condutores/:slug/editar',
    motivo: 'Edição de condutor alcançada pelo detalhe do cadastro, não pelo menu.',
  },
  {
    caminho: '/operacao/monitores/:slug',
    motivo: 'Detalhe de monitor alcançado pela linha da listagem operacional, não pelo menu.',
  },
  {
    caminho: '/operacao/monitores/:slug/editar',
    motivo: 'Edição de monitor alcançada pelo detalhe do cadastro, não pelo menu.',
  },
  {
    caminho: '/operacao/veiculos/:slug',
    motivo: 'Detalhe de veículo alcançado pela linha da listagem operacional, não pelo menu.',
  },
  {
    caminho: '/veiculos/:slug/editar',
    motivo: 'Edição de veículo alcançada pelo detalhe do cadastro, não pelo menu.',
  },
  {
    caminho: '/operacao/titulares/:slug',
    motivo:
      'Detalhe consultivo de titular alcançado pela linha da listagem operacional, não pelo menu.',
  },
  /*
   * Detalhe, criação e edição de Escolas e Seguradoras. São alcançadas DE
   * DENTRO da lista de cada cadastro — a lista, sim, está no menu. Pôr "nova" e
   * "editar" na barra lateral encheria o menu de verbo em vez de assunto, e
   * ofereceria criar antes de a pessoa ver o que já existe.
   */
  {
    caminho: '/admin/parametros/referencia/escolas/nova',
    motivo: 'Criação de escola: alcançada pela lista de escolas, que está no menu.',
  },
  {
    caminho: '/admin/parametros/referencia/escolas/:slug',
    motivo: 'Detalhe de uma escola: alcançado pela linha da lista, que está no menu.',
  },
  {
    caminho: '/admin/parametros/referencia/escolas/:slug/editar',
    motivo: 'Edição de uma escola: alcançada pelo detalhe dela, nunca direto do menu.',
  },
  {
    caminho: '/admin/parametros/referencia/seguradoras/nova',
    motivo: 'Criação de seguradora: alcançada pela lista de seguradoras, que está no menu.',
  },
  {
    caminho: '/admin/parametros/referencia/seguradoras/:slug',
    motivo: 'Detalhe de uma seguradora: alcançado pela linha da lista, que está no menu.',
  },
  {
    caminho: '/admin/parametros/referencia/seguradoras/:slug/editar',
    motivo: 'Edição de uma seguradora: alcançada pelo detalhe dela, nunca direto do menu.',
  },
  {
    caminho: '/jornadas/j02/s03-consultar-publico',
    motivo:
      'Consulta pública pelo código de uma publicação, sem sessão interna: alcançada pelo link do cabeçalho de /publicacoes, que desde a decisão D2 do dono (2026-09-30) é consulta fixa da página Legislação.',
  },
  {
    caminho: '/jornadas/j02/s04-acompanhar-protocolo',
    motivo:
      'Consulta anônima por protocolo e CPF ou CNPJ (J-02/S04), que o critério de aceite exige sem conta; alcançada pelo cartão de entrar de /credenciados/acompanhamento (D4, 2026-09-30).',
  },
  {
    caminho: '/jornadas/j03/s02-identificar-rascunho',
    motivo:
      'Endereço antigo, redireciona para /credenciados/autocadastro desde 2026-09-30: links já enviados continuam funcionando.',
  },
  {
    caminho: '/jornadas/j03/s03-protocolar-inscricao',
    motivo:
      'Endereço antigo, redireciona para /credenciados/autocadastro desde 2026-09-30: links já enviados continuam funcionando.',
  },
  {
    caminho: '/jornadas/j03/s04-preparar-consentimento',
    motivo:
      'Endereço antigo, redireciona para /credenciados/autocadastro desde 2026-09-30 (consentimento de terceiro retirado do escopo): links já enviados continuam funcionando.',
  },
  {
    caminho: '/jornadas/j03/s04-consentir-terceiro',
    motivo:
      'Endereço antigo, redireciona para / desde 2026-09-30 (consentimento de terceiro retirado do escopo): o link do convite já enviado por e-mail continua levando ao portal.',
  },
  {
    caminho: '/jornadas/j03/s05-corrigir-inscricao',
    motivo:
      'Endereço antigo, redireciona para /credenciados/acompanhamento desde 2026-09-30: links já enviados continuam funcionando.',
  },
  {
    caminho: '/jornadas/j03/s06-cumprir-convocacao',
    motivo:
      'Endereço antigo, redireciona para /credenciados/acompanhamento desde 2026-09-30: links já enviados continuam funcionando.',
  },
  {
    caminho: '/jornadas/j05/s02-confirmar-titular',
    motivo: 'Titular confirma vínculo de recurso por link recebido, sem sessão interna.',
  },
  {
    caminho: '/portal/credenciado/veiculos',
    motivo:
      'Cadastro de veículo pela API real (J05-S01, verbo cadastrar): é a única interface de cadastro do credenciado, porque /credenciado/veiculos lê a mesma API só para leitura. Fica até a escrita ter destino no alvo Ciclo do credenciado (decisão P-2 de 2026-10-02). Sem link no portal.',
  },
  {
    caminho: '/portal/credenciado/manutencao-recursos',
    motivo:
      'Manutenção de recurso pela API real (J05-S05, verbo submeter), sem par em /credenciado: fica até ter destino decidido no alvo Ciclo do credenciado. Sem link no portal.',
  },
  {
    caminho: '/portal/credenciado/vinculos',
    motivo:
      'Apresentação de vínculo de recurso pela API real (J05-S02, verbo apresentar), sem par em /credenciado: fica até ter destino decidido no alvo Ciclo do credenciado. Sem link no portal.',
  },
  {
    caminho: '/portal/credenciado/condutores',
    motivo:
      'Cadastro de condutor pela API real (J05-S01, verbo cadastrar): /credenciado/condutores ainda lê mock e, quando ler a API, esta rota sai na mesma PR (um caminho por tela). Só a pista de navegação da própria API de J05-S01 aponta para cá.',
  },
  {
    caminho: '/portal/credenciado/monitores',
    motivo:
      'Cadastro de monitor pela API real (J05-S01, verbo cadastrar): é a única interface de cadastro do credenciado, porque /credenciado/monitores lê a mesma API só para leitura. Fica até a escrita ter destino no alvo Ciclo do credenciado (decisão P-2 de 2026-10-02). Sem link no portal.',
  },
  {
    caminho: '/portal/credenciado/ordens-ativas',
    motivo:
      'Endereço antigo, redireciona a Contratos e Termos; o link dos e-mails de OS continua funcionando.',
  },
  {
    caminho: '/portal/credenciado/ordens-em-execucao',
    motivo:
      'Endereço antigo, redireciona a Contratos e Termos; o link que as pistas de navegação da API (J06-S05 e J08-S07) davam antes continua funcionando.',
  },
  {
    caminho: '/portal/credenciado/mudancas-os',
    motivo:
      'Pedido de mudança de OS pela API real (J06-S05, verbo solicitar_mudanca), sem par em /credenciado. Ganha link do detalhe da OS quando a vigência literal da tela for corrigida; sem link até lá.',
  },
  {
    caminho: '/portal/credenciado/demonstrativos-pagamento',
    motivo:
      'Demonstrativos de pagamento pela API real (J08-S07, só leitura); o par /credenciado/financeiro ainda lê mock. Sai na PR que ligar o par à API (um caminho por tela). Sem link até lá.',
  },
  {
    caminho: '/portal/credenciado/recursos',
    motivo:
      'Defesa contra penalidade pela API real (J09-S03, verbo protocolar_defesa): única interface de escrita, porque /credenciado/recursos ainda lê mock e só lista. Sai na PR que ligar o par à API (um caminho por tela). Sem link até lá.',
  },
  {
    caminho: '/portal/credenciado/cobrancas',
    motivo:
      'Consulta de cobrança pela API real (J09-S05, só leitura); o par /credenciado/cobrancas ainda lê mock. Sai na PR que ligar o par à API (um caminho por tela). Sem link até lá.',
  },
  {
    caminho: '/portal/solicitacoes/nova',
    motivo:
      'Protocolo de pedido pela API real (J10-S01, verbo protocolar_pedido); o par /credenciado/solicitacoes/nova ainda é mock. Item do PRD já entregue: sai só quando o par cumprir o mesmo critério de aceite (decisão D3 de 2026-09-30). Sem link até lá.',
  },
  {
    caminho: '/portal/solicitacoes/complementar',
    motivo:
      'Complemento de pedido pela API real (J10-S02, verbo enviar_complemento); /credenciado/solicitacoes ainda é mock e não tem essa ação. Item do PRD já entregue: sai só quando o par cumprir o mesmo critério de aceite (decisão D3 de 2026-09-30). Sem link até lá.',
  },
  {
    caminho: '/portal/solicitacoes/acompanhar',
    motivo:
      'Acompanhamento e cancelamento de pedido pela API real (J10-S04, verbo cancelar_pedido); o par /credenciado/solicitacoes ainda é mock. Item do PRD já entregue: sai só quando o par cumprir o mesmo critério de aceite (decisão D3 de 2026-09-30). Sem link até lá.',
  },
  {
    caminho: '/primeiro-acesso',
    motivo:
      'Etapa do fluxo de entrada, alcançada a partir do login por quem ainda não tem senha definida.',
  },
  { caminho: '*', motivo: 'Endereço inexistente. Por definição não está no menu.' },
  {
    caminho: '/admin/emails/templates/:id',
    motivo:
      'Detalhe de um template, alcançado por clique na aba Templates de /admin/emails, não pelo menu.',
  },
  {
    caminho: '/admin/usuarios/convidar',
    motivo: 'Convite de pessoa: alcançado pelo botão "Convidar pessoa" da lista de usuários.',
  },
  {
    caminho: '/admin/usuarios/:id',
    motivo:
      'Tela interna de uma pessoa: alcançada pela linha da lista de usuários, que está no menu.',
  },
  {
    caminho: '/admin/usuarios/:id/editar',
    motivo:
      'Edição de uma pessoa: alcançada pelo botão Editar da tela interna, nunca direto do menu.',
  },
  {
    caminho: '/admin/perfis/:id/usuarios',
    motivo: 'Detalhe de um perfil de acesso, alcançado por clique em /admin/perfis, não pelo menu.',
  },
  {
    caminho: '/operacao/financeiro/cobrancas/:id',
    motivo:
      'Detalhe de uma cobrança DAMSP: alcançado a partir da linha em "Cobranças DAMSP", nunca por item de menu próprio.',
  },
  {
    caminho: '/admin/perfis/novo',
    motivo:
      'Ação de criar um perfil de acesso, alcançada pelo botão "Novo perfil" dentro de "Perfis de Acesso" — nunca por item de menu próprio.',
  },
  {
    caminho: '/admin/perfis/:id',
    motivo:
      'Tela de edição de um perfil de acesso customizado, alcançada pelo clique na linha da listagem em "Perfis de Acesso" — regra de interface 9 do CLAUDE.md, não é item de menu próprio.',
  },
  {
    caminho: '/admin/perfis/sistema/:role',
    motivo:
      'Visualização somente leitura de um perfil de papel fixo do sistema (ADMIN, GESTOR etc.), alcançada pelo clique na linha da listagem em "Perfis de Acesso" — não é item de menu próprio.',
  },
  {
    caminho: '/admin/portal/paginas/:slug',
    motivo:
      'Tela de edição de uma página do portal público, alcançada pelo clique na linha da listagem em "Páginas do Portal" — regra de interface 9 do CLAUDE.md, não é item de menu próprio.',
  },
  {
    caminho: '/operacao/credenciados/:slug',
    motivo: 'Detalhe de credenciado alcançado pela linha da listagem operacional, não pelo menu.',
  },
  {
    caminho: '/operacao/credenciados/:slug/editar',
    motivo: 'Edição de credenciado alcançada pelo detalhe do cadastro, não pelo menu.',
  },
  /*
   * Seis das sete telas de configuração operacional (ONDA-T1) eram item de menu
   * próprio; no protótipo são cards dentro da página hub `/admin/configuracoes`
   * (`ConfiguracoesHub.tsx`). Alcançadas pelo card do hub, não pela barra
   * lateral. A sétima, `/gerais`, é também o item "Configurações Globais" do
   * menu, igual ao protótipo, e por isso não está aqui.
   */
  {
    caminho: '/admin/configuracoes/fechamento',
    motivo: 'Card dentro do hub "Configurações" (ConfiguracoesHub.tsx), não item de menu próprio.',
  },
  {
    caminho: '/admin/configuracoes/desvio-alerta',
    motivo: 'Card dentro do hub "Configurações" (ConfiguracoesHub.tsx), não item de menu próprio.',
  },
  {
    caminho: '/admin/configuracoes/ateste',
    motivo: 'Card dentro do hub "Configurações" (ConfiguracoesHub.tsx), não item de menu próprio.',
  },
  {
    caminho: '/admin/configuracoes/slas',
    motivo: 'Card dentro do hub "Configurações" (ConfiguracoesHub.tsx), não item de menu próprio.',
  },
  {
    caminho: '/admin/configuracoes/acoes-em-lote',
    motivo: 'Card dentro do hub "Configurações" (ConfiguracoesHub.tsx), não item de menu próprio.',
  },
  {
    caminho: '/admin/configuracoes/agendamentos',
    motivo: 'Card dentro do hub "Configurações" (ConfiguracoesHub.tsx), não item de menu próprio.',
  },
];
